import React, { createContext, useContext, useEffect, useState } from 'react';
import { de, enGB } from 'date-fns/locale';

const LANG_KEY = 'life_manager_lang';

// The chosen language, readable outside React (widget bridge, helpers that run
// before/without the provider). localStorage is the single source of truth —
// the provider seeds itself from it and writes back on every change.
export const getLang = () => {
  try {
    const stored = window.localStorage.getItem(LANG_KEY);
    if (stored === 'en' || stored === 'de') return stored;
  } catch (_) {}
  return 'de';
};

// date-fns locale for the current language. Every `format(..., { locale })`
// call in the app goes through this so month and weekday names follow the
// language setting instead of always being German. enGB keeps Monday as the
// first day of the week, which is what the calendar grid assumes.
export const localeFor = (lang) => (lang === 'en' ? enGB : de);
export const getDateLocale = () => localeFor(getLang());

// Mo…So / Mon…Sun for the calendar grid header, Monday first. Derived from the
// locale so the app calendar and the home-screen widget always agree.
// `short` is the two-letter form: Mo Di Mi Do Fr Sa So / Mo Tu We Th Fr Sa Su.
export const weekdayLabels = (locale = getDateLocale()) =>
  Array.from({ length: 7 }, (_, i) =>
    locale.localize.day((i + 1) % 7, { width: 'short' })
  );

// Translation dictionary. Keys are dot-paths; values may include {placeholder}s.
// Add new keys as components are translated.
const dict = {
  en: {
    // App / shell ---------------------------------------------------------
    'app.title': 'Life Manager',
    'app.tagline': 'Track, Budget & Plan',
    'app.loading': 'Loading…',
    'app.settings': 'Settings',
    'app.signOut': 'Sign out',
    'app.syncError': 'Sync error: {message}',
    'app.closeMenu': 'Close menu',
    'app.joinedCalendar': 'Joined calendar',
    'app.joinedCalendarNamed': 'Joined calendar “{name}”',
    'app.joinedFuelLog': 'Joined fuel log',
    'app.joinFuelLogFailed': 'Could not join the fuel log: {message}',
    'app.joinedShoppingList': 'Joined shopping list',
    'app.joinShoppingListFailed': 'Could not join the shared shopping list: {message}',

    // Common --------------------------------------------------------------
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.back': 'Back',
    'common.confirm': 'Confirm',
    'common.close': 'Close',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.loading': 'Loading…',
    'common.uploading': 'Uploading…',
    'common.search': 'Search',
    'common.today': 'Today',
    'common.yesterday': 'Yesterday',
    'widget.openApp': 'Open Eulea to load the calendar.',
    'widget.todos': 'To-dos',
    'widget.allDone': 'All done',
    'widget.open': 'open',
    'widget.birthdays': 'Birthdays',
    'widget.noBirthdays': 'No birthdays',
    'widget.budget': 'Budget',
    'widget.income': 'Income',
    'widget.expenses': 'Expenses',
    'widget.remaining': 'Left',
    'widget.timer': 'Time tracker',
    'widget.timerIdle': 'No timer running',
    'widget.start': 'Start',
    'widget.stop': 'Stop',
    'widget.shopping': 'Shopping list',
    'widget.shoppingEmpty': 'Nothing to buy',
    'widget.nextUp': 'Next up',
    'widget.nothingUpcoming': 'Nothing coming up',
    'widget.allDay': 'all day',
    'widget.agenda': 'Upcoming',
    'widget.added': 'Added',
    'widget.timerStarted': 'Timer running.',
    'widget.timerStopped': 'Timer stopped.',
    'calendar.editSeriesPrompt': 'This event repeats. What do you want to change?',
    'calendar.editOnlyThis': 'Only this event',
    'calendar.editThisAndFuture': 'This and all future events',
    'calendar.editAll': 'All events in the series',
    'calendar.prevMonth': 'Previous month',
    'calendar.nextMonth': 'Next month',
    'calendar.prevYear': 'Previous year',
    'calendar.nextYear': 'Next year',
    'calendar.holidays': 'Austrian public holidays',
    'calendar.holidaysHint': 'Show the gesetzliche Feiertage in the calendar. The label colour you pick is reserved for them, like the birthday label.',
    'calendar.holidaysLabel': 'Holidays',
    'calendar.labelColor': 'Label colour',
    'common.tomorrow': 'Tomorrow',
    'common.upcoming': 'Upcoming',
    'common.all': 'All',
    'common.completed': 'Completed',
    'common.untitled': 'Untitled',
    'common.optional': 'optional',
    'common.note': 'Note',
    'common.priority': 'Priority',
    'common.date': 'Date',
    'common.title': 'Title',
    'common.name': 'Name',
    'common.new': 'New',
    'common.color': 'Color',
    'common.total': 'Total',
    'common.unknown': 'Unknown',
    'common.notes': 'Notes',
    'common.notesOptional': 'Notes (optional)',
    'common.end': 'End',
    'common.create': 'Create',
    'common.saving': 'Saving…',
    'common.saved': 'Saved',
    'common.updated': 'Updated',
    'common.deleted': 'Deleted',
    'common.error': 'Error',
    'common.saveFailed': 'Saving failed',
    'common.deleteFailed': 'Deleting failed',
    'common.loadFailed': 'Loading failed',
    'common.couldNotSave': 'Could not save',
    'common.renameFailed': 'Renaming failed',
    'common.renameList': 'Rename list',
    'common.leaveList': 'Leave list',
    'common.deleteList': 'Delete list',
    'common.newName': 'New name',
    'common.deleteEntryTitle': 'Delete entry?',
    'common.noEntriesYet': 'No entries yet',
    'common.history': 'History',
    'common.durationMinutes': 'Duration (minutes)',
    'common.amountEur': 'Amount (€)',
    'common.errAmountInvalid': 'Invalid amount',
    'common.errDateMissing': 'Date is missing',
    'common.errNameMissing': 'Name is missing',

    // Auth ----------------------------------------------------------------
    'auth.signInTitle': 'Sign in to your account',
    'auth.signUpTitle': 'Create a new account',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.signIn': 'Sign in',
    'auth.signUp': 'Sign up',
    'auth.noAccount': 'No account?',
    'auth.haveAccount': 'Already have an account?',
    'auth.pleaseWait': 'Please wait…',
    'auth.failed': 'Authentication failed',
    'auth.confirmEmail': 'Account created. Check your email to confirm, then sign in.',
    'onboarding.nameTitle': 'Welcome!',
    'onboarding.nameHint': 'Choose a display name. It will be shown to members of shared calendars.',
    'onboarding.continue': 'Continue',

    // Settings ------------------------------------------------------------
    'settings.title': 'Settings',
    'settings.menuOrder': 'Menu order',
    'settings.menuOrderHint': 'Move main groups up or down with the arrows.',
    'settings.enabledFeatures': 'Enabled features',
    'settings.enabledFeaturesHint': 'Enable or disable features individually. Click a feature for more info.',
    'settings.moveUp': 'Move up',
    'settings.moveDown': 'Move down',
    'settings.localFound': 'Local data found',
    'settings.localFoundHint':
      'You have data from the local version of this app in your browser. Click the button to import it into the cloud.',
    'settings.importLocal': 'Import local data into cloud',
    'settings.importing': 'Importing…',
    'settings.importSuccess': '✅ Import successful!',
    'settings.importFailed': '❌ Import failed.',
    'settings.confirmMigrate':
      'Import data from local browser storage into the cloud? Existing cloud data of this account will be merged with local data (matching IDs will be overwritten).',
    'settings.backup': 'Backup',
    'settings.backupHint': 'Export all your data as a JSON file or import a previous backup.',
    'settings.exportJson': 'Export JSON',
    'settings.importJson': 'Import JSON',
    'settings.language': 'Language',
    'settings.languageHint': 'Choose the language for the entire app.',
    'settings.languageEnglish': 'English',
    'settings.languageGerman': 'German',
    'settings.appearance': 'Appearance',
    'settings.appearanceHint': 'Choose your theme mode and a personal accent color.',
    'settings.themeMode': 'Theme',
    'settings.themeLight': 'Light',
    'settings.themeDark': 'Dark',
    'settings.themeSystem': 'System',
    'settings.primaryColor': 'Accent color',
    'settings.showPersonalCalendar': 'Show personal calendar',
    'settings.showPersonalCalendarHint': 'Hide the personal calendar from the sidebar (data is kept).',
    'settings.sharedCalendarsVisibility': 'Shared calendars',
    'settings.profile': 'Profile',
    'settings.profileHint': 'Your name and avatar are shown to members of shared calendars.',
    'settings.profileName': 'Display name',
    'settings.profileNamePlaceholder': 'e.g. Jana',
    'settings.profileNoName': 'No name set',
    'settings.profileColor': 'Avatar color',
    'settings.profileEmoji': 'Avatar',
    'settings.profileEmojiNone': 'Use first letter',
    'settings.profileSaved': 'Saved',
    'settings.profileVisibility': 'Visible to calendar members',
    'settings.profileKindEmoji': 'Avatar',
    'settings.profileKindPhoto': 'Photo',
    'settings.profilePhotoUpload': 'Upload photo',
    'settings.profilePhotoReplace': 'Replace photo',
    'settings.profilePhotoUploading': 'Uploading…',
    'settings.profilePhotoRemove': 'Remove photo',
    'settings.profilePhotoHint': 'JPG or PNG, max 5 MB. Public to calendar members.',
    'settings.profilePhotoTooLarge': 'Image is too large (max 5 MB).',
    'sharedCalendar.createTitle': 'Create shared calendar',
    'sharedCalendar.namePlaceholder': 'Calendar name (e.g. Family)',
    'sharedCalendar.create': 'Create',
    'sharedCalendar.createButton': 'Create shared calendar',
    'sharedCalendar.shareTitle': 'Share this calendar',
    'sharedCalendar.shareHint': 'Anyone signed in who opens this link is added as a member.',
    'sharedCalendar.copyLink': 'Copy link',
    'sharedCalendar.copied': 'Copied!',
    'sharedCalendar.delete': 'Delete calendar',
    'sharedCalendar.deleteConfirm': 'Delete this shared calendar for everyone? This cannot be undone.',
    'sharedCalendar.leave': 'Leave calendar',
    'sharedCalendar.leaveConfirm': 'Leave this shared calendar? You will lose access to its events.',
    'sharedCalendar.joinSuccess': 'Joined shared calendar',
    'sharedCalendar.joinFailed': 'Could not join calendar: {message}',
    'sharedCalendar.owner': 'Owner',
    'sharedCalendar.member': 'Member',
    'sharedCalendar.settings': 'Calendar settings',
    'sharedCalendar.membersTitle': 'Members',
    'sharedCalendar.invite': 'Invite link',
    'sharedCalendar.loadingMembers': 'Loading members…',
    'sharedCalendar.you': 'You',
    'diaryLock.title': 'Protect diary',
    'diaryLock.hint': 'Ask for Face ID or a code before the diary opens. Applies on all your devices.',
    'diaryLock.enable': 'Lock the diary',
    'diaryLock.enableHint': 'Locks again when you leave the app, and after 5 minutes.',
    'diaryLock.biometrics': 'Unlock with Face ID',
    'diaryLock.biometricsHint': 'Face ID or Touch ID, whatever this iPhone has.',
    'diaryLock.bioUnavailable': 'Face ID is not available on this device.',
    'diaryLock.code': 'Code',
    'diaryLock.codeSet': 'A code is set.',
    'diaryLock.codeNotSet': 'No code set.',
    'diaryLock.codeRules': '4 to 8 digits.',
    'diaryLock.setCode': 'Set code',
    'diaryLock.changeCode': 'Change code',
    'diaryLock.removeCode': 'Remove code',
    'diaryLock.removeCodeConfirm': 'Remove the diary code?',
    'diaryLock.repeatCode': 'Repeat the code',
    'diaryLock.codeInvalid': 'The code must be 4 to 8 digits.',
    'diaryLock.codeMismatch': 'The codes do not match.',
    'diaryLock.noMethod': 'Neither Face ID nor a code is set up, so the diary is not locked yet.',
    'diaryLock.noMethodBanner': 'The diary lock is on, but Face ID is not available here and no code is set. Set a code in Settings.',
    'diaryLock.lockedTitle': 'Diary locked',
    'diaryLock.lockedHint': 'Unlock to read and write entries.',
    'diaryLock.unlock': 'Unlock',
    'diaryLock.wrongCode': 'Wrong code.',
    'diaryLock.useFaceId': 'Unlock with Face ID',
    'diaryLock.reason': 'Unlock your diary',
    'diaryLock.bioFailed': 'Face ID did not work. Try again.',
    'diaryLock.bioFailedWithCode': 'Face ID did not work. Try again or enter your code.',
    'notify.title': 'Notifications',
    'notify.digest': 'Daily overview',
    'notify.digestHint': 'One message at the chosen time listing everything on the calendar that day.',
    'notify.reminder': 'Reminder before events',
    'notify.off': 'Off',
    'notify.none': 'None',
    'notify.minutes': '{n} min before',
    'notify.hours': '{n} h before',
    'notify.oneDay': '1 day before',
    'notify.days': '{n} days before',
    'notify.birthdayAhead': 'Birthday heads-up',
    'notify.birthdayAheadHint': 'At the daily overview time, otherwise at 09:00.',
    'notify.sharedHint': 'Applies to every member of this calendar. Anyone can mute it under Settings → Notifications.',
    'notify.personalCalendar': 'Personal calendar',
    'notify.personalHint': 'Reminders and the daily overview for your own events.',
    'notify.rowReminders': 'Reminders before events',
    'notify.rowDigest': 'Daily overview',
    'notify.settingsTitle': 'Notification settings',
    'notify.settingsHint': 'Reminders, daily overview and changes by other members – per calendar. Applies to all your devices.',
    'notify.muteAll': 'Mute all push notifications',
    'notify.muteAllHint': 'Overrides everything below.',
    'notify.noSharedCalendars': "You haven't joined any shared calendars yet.",
    'notify.mutedBadge': 'muted',
    'notify.rowCreated': 'New events',
    'notify.rowUpdated': 'Changes',
    'notify.rowCommented': 'Comments',
    'notify.rowDeleted': 'Deletions',
    'settings.pushTitle': 'Push notifications',
    'settings.pushHint': 'Reminders before events, a daily overview and changes in shared calendars – on this device.',
    'settings.pushTest': 'Send test notification',
    'settings.pushFailed': 'Failed',
    'settings.pushTestSentOne': 'Sent to 1 device. Check for the notification.',
    'settings.pushTestSentMany': 'Sent to {n} devices. Check for the notification.',
    'settings.pushTestNoSubs': 'No subscriptions found. Toggle notifications on first.',
    'settings.pushTestFailed': 'Failed to send test',
    'settings.pushTestTitle': 'Eulea test',
    'settings.pushTestBody': 'Push notifications are working 🎉',
    'settings.pushUnsupported': 'Your browser doesn’t support push notifications. On iPhone, add Eulea to your home screen first — notifications only work from the installed app.',
    'settings.pushBlockedNative': 'Notifications are turned off for Eulea. iOS Settings → Eulea → Notifications → Allow, then switch this on here.',
    'settings.pushBlockedBrowser': 'Notifications are blocked in your browser settings. Allow them for this site, then come back and toggle this on.',
    'settings.profileSaveFailed': 'Save failed',
    'settings.profileUploadFailed': 'Upload failed',
    'sharedCalendar.createdBy': 'Created by',
    'sharedCalendar.unknownUser': 'Unknown user',
    'sharedCalendar.participants': 'Participants',
    'sharedCalendar.addParticipant': 'Add…',
    'sharedCalendar.allAdded': 'Everyone is already a participant.',
    'sharedCalendar.withBirthdays': 'Add a birthday list',
    'sharedCalendar.withBirthdaysHint': 'A separate birthday list just for this calendar. All members can add and edit entries.',
    'sharedCalendar.birthdays': 'Birthdays',
    'sharedCalendar.manageBirthdays': 'Manage birthdays',
    'sharedCalendar.birthdaysSection': 'Birthday list',
    'sharedCalendar.enableBirthdays': 'Enable birthday list',
    'sharedCalendar.enableBirthdaysHint': 'Shared with every member of this calendar.',
    'sharedCalendar.backToCalendar': 'Back to calendar',
    'sharedCalendar.myLabel': 'Your label in this calendar',
    'sharedCalendar.myLabelHint': 'Used automatically when you create a new event here.',
    'sharedCalendar.createFailed': 'Could not create the calendar',
    'sharedCalendar.leaveFailed': 'Could not leave the calendar',
    'sharedCalendar.deleteFailed': 'Could not delete the calendar',
    'color.blue': 'Blue',
    'color.lightBlue': 'Light blue',
    'color.green': 'Green',
    'color.pink': 'Pink',
    'color.lightPink': 'Light pink',
    'color.yellow': 'Yellow',
    'color.orange': 'Orange',
    'color.black': 'Black',
    'color.white': 'White',

    // Sidebar / feature groups --------------------------------------------
    'nav.more': 'More',
    'nav.allPages': 'All pages',
    'settings.account': 'Your account',
    'settings.deleteAccount': 'Delete account',
    'settings.deletingAccount': 'Deleting…',
    'settings.deleteAccountHint':
      'Deleting removes your login and everything in it — calendars, budget, diary, photos. This cannot be undone.',
    'settings.deleteAccountConfirm':
      'Permanently delete your account and all of its data? This cannot be undone.',
    'settings.deleteAccountTypeTitle': 'Type DELETE to confirm',
    'settings.deleteAccountWord': 'DELETE',
    'settings.deleteAccountFailed': 'Could not delete the account: {message}',
    'settings.tabBar': 'Tab bar',
    'settings.tabBarHint': 'Pick up to {n} pages for the bar at the bottom of the screen. Everything else stays under "More".',
    'nav.dashboard': 'Dashboard',
    'nav.planning': 'Planning',
    'nav.calendar': 'Calendar',
    'nav.birthdays': 'Birthday Calendar',
    'nav.todo': 'Todo',
    'nav.reminders': 'Reminders',
    'nav.finance': 'Finance',
    'nav.budget': 'Budget',
    'nav.savings': 'Savings',
    'nav.fitness': 'Fitness',
    'nav.fitnessOverview': 'Overview',
    'nav.sports': 'Sports',
    'nav.personal': 'Personal',
    'nav.diary': 'Diary',
    'nav.habits': 'Habit Tracker',
    'nav.work': 'Work',
    'nav.timetracker': 'Time Tracker',
    'nav.shopping': 'Shopping List',
    'nav.fuel': 'Fuel Log',
    'nav.tutoring': 'Tutoring',
    'nav.projects': 'Projects',

    'navDesc.dashboard':
      'Weekly overview with all upcoming appointments, birthdays and a quick look at your most important data.',
    'navDesc.planning': 'All about planning: calendar, birthdays, tasks and reminders.',
    'navDesc.calendar': 'Monthly view with events, colored labels and recurrences.',
    'navDesc.birthdays': 'Manage birthdays separately and sync them with the main calendar.',
    'navDesc.todo': 'Simple to-do list for your tasks.',
    'navDesc.reminders': 'Reminders for important things.',
    'navDesc.finance': 'All about money: budget, savings and accounts.',
    'navDesc.budget': "This month's income and expenses and trends.",
    'navDesc.savings': 'Track savings goals, accounts and saving rates.',
    'navDesc.fitness':
      'Track workouts, personal bests and plan recurring training via the calendar.',
    'navDesc.fitnessOverview': 'Active sports and statistics.',
    'navDesc.sports':
      'Detail views per sport including workouts, PRs and workout structures.',
    'navDesc.personal': 'Personal: diary, habits and more.',
    'navDesc.diary': 'Personal diary.',
    'navDesc.habits': 'Track habits and build streaks.',
    'navDesc.work': 'Tools for work: time tracking and more.',
    'navDesc.timetracker': 'Track working time.',
    'navDesc.shopping': 'Shopping lists with recipes and quantity calculation.',
    'navDesc.fuel': 'Shared fuel log: track odometer readings and split fill-up costs by km driven.',
    'navDesc.tutoring': 'Track tutoring sessions per student and semester. Mark sessions as paid when settled.',
    'navDesc.projects': 'Per-project P&L ledger: log every expense and income, see cumulative balance and break-even per project.',

    // Diary ---------------------------------------------------------------
    'diary.title': 'Diary',
    'diary.subtitle': 'Personal diary',
    'diary.newEntry': 'New entry',
    'diary.firstEntry': 'Create your first entry',
    'diary.empty': 'No entries yet. Start your diary!',
    'diary.titlePlaceholder': 'Title…',
    'diary.bodyPlaceholder': 'Write your entry here…',
    'diary.addPhoto': 'Add photo',
    'diary.removePhoto': 'Remove photo',
    'diary.deleteConfirm': 'Delete entry?',
    'diary.morePhotos': '+{count} more photos',
    'diary.uploadFailed': 'Photo upload failed: {message}',
    'diary.showTitleInList': 'Show title in list',
    'diary.showTitleInListHint': 'If off, the list shows only the date for this entry.',

    // Todo ----------------------------------------------------------------
    'todo.title': 'Todo',
    'todo.subtitle': 'Manage tasks',
    'todo.newTaskPlaceholder': 'What needs to be done?',
    'todo.notePlaceholder': 'Additional info…',
    'todo.allGroups': 'All groups',
    'todo.newGroup': 'New group',
    'todo.newGroupShort': 'New',
    'todo.manageGroups': 'Manage groups',
    'todo.groupNamePrompt': 'Name of the new group',
    'todo.priorityHigh': 'High',
    'todo.priorityMedium': 'Medium',
    'todo.priorityLow': 'Low',
    'todo.viewToday': 'Today',
    'todo.viewUpcoming': 'Upcoming',
    'todo.viewAll': 'All',
    'todo.viewCompleted': 'Completed',
    'todo.editTitle': 'Edit task',
    'todo.newTitle': 'New task',
    'todo.dateOptional': 'Date (optional)',
    'todo.priorityOptional': 'Priority (optional)',
    'todo.groupOptional': 'Group (optional)',
    'todo.noteOptional': 'Note (optional)',
    'todo.noGroup': 'None',
    'todo.markDone': 'Mark done',
    'todo.markOpen': 'Mark as open',
    'todo.overdue': 'overdue',
    'todo.emptyToday': 'No tasks for today.',
    'todo.emptyUpcoming': 'No upcoming tasks.',
    'todo.emptyAll': 'No tasks yet.',
    'todo.emptyCompleted': 'Nothing completed yet.',
    'todo.noGroups': 'No groups yet.',
    'todo.noDate': 'No date',
    'todo.tasksOne': 'task',
    'todo.tasksOther': 'tasks',
    'todo.confirmDelete': 'Delete task?',
    'todo.deleteGroupConfirm':
      'Delete this group? Tasks in it will be kept (without a group).',
    'todo.timeOptional': 'Time (optional)',
    'todo.showInCalendar': 'Show in calendar',
    'todo.labelColor': 'Label color',
    'todo.calendarTarget': 'Calendar',
    'todo.personalCalendar': 'Personal calendar',
    'todo.inUse': 'in use',
    'todo.calendarHint':
      'Tasks with a date appear in the calendar with a task icon in the selected color; completed ones are crossed out.',
    'todo.calendarSharedHint':
      'Members of this calendar can see these tasks.',

    // Dashboard widgets -----------------------------------------------------
    'dashboard.subtitle': "Welcome back! Here's your life overview.",
    'dashboard.customize': 'Customize',
    'dashboard.doneEditing': 'Done',
    'dashboard.reset': 'Reset layout',
    'dashboard.editHint': 'Drag widgets by their header, resize at the bottom-right corner, remove with the ×.',
    'dashboard.allDay': 'all day',
    'dashboard.emptyDay': 'Nothing planned for today.',
    'dashboard.emptyTodos': 'No open tasks.',
    'dashboard.emptyShopping': 'Shopping list is empty.',
    // Gallery
    'dashw.widget': 'Widget',
    'dashw.galleryTitle': 'Add widget',
    'dashw.searchWidgets': 'Search widgets',
    'dashw.noResults': 'Nothing found.',
    'dashw.styleCount': '{n} styles',
    'dashw.pickInstance': 'Which one?',
    'dashw.pickSize': 'Size',
    'dashw.resizeHint': 'You can resize it any time later.',
    'dashw.addWidget': 'Add widget',
    'dashw.removeWidget': 'Remove widget',
    'dashw.emptyDashboard': 'No widgets on the dashboard yet.',
    'dashw.resetConfirm': 'Reset the dashboard to the default widgets?',
    'dashw.sizeSmall': 'Small',
    'dashw.sizeMedium': 'Medium',
    'dashw.sizeLarge': 'Large',
    'dashw.myList': 'My list',
    'dashw.allAccounts': 'All accounts',
    'dashw.allSports': 'All sports',
    // Catalog: styles + descriptions
    'calendar.suggestLast': 'Last used {date}',
    'calendar.editingSeries': 'Editing a repeating event',
    'calendar.clickedOn': 'Clicked on',
    'calendar.seriesStart': 'Series start',
    'calendar.todosOf': "{name}'s to-dos",
    'calendar.fromTodosOf': 'from to-dos ({name})',
    'calendar.fromTodos': 'from to-dos',
    'calendar.activity': 'Activity',
    'calendar.noActivity': 'No activity yet.',
    'calendar.noActivityHint': 'Changes and comments in shared calendars show up here.',
    'calendar.eventFallback': 'Event',
    'calendar.fieldLabel': 'Label',
    'calendar.recurrence': 'Repeat',
    'calendar.auditCreated': 'Event created',
    'calendar.auditUpdated': 'Event updated',
    'calendar.auditFieldsUpdated': '{fields} updated',
    'calendar.deleteEventConfirm': 'Delete event?',
    'calendar.allDay': 'All day',
    'calendar.event': 'Event',
    'calendar.labels': 'Labels',
    'calendar.manageLabels': 'Manage labels',
    'calendar.nothingPlanned': 'Nothing planned.',
    'calendar.weekShort': 'Wk',
    'calendar.moreCount': '+{n} more',
    'calendar.calendarView': 'Calendar view',
    'calendar.loadSettingsFailed': 'Failed to load calendar settings',
    'calendar.commentPlaceholder': 'Comment…',
    'calendar.send': 'Send',
    'calendar.attachments': 'Attachments',
    'calendar.auditDeleted': 'Event deleted',
    'calendar.typeTime': 'Type a time',
    'calendar.collapseHeader': 'Collapse details',
    'calendar.expandHeader': 'Show details',
    'calendar.pickTime': 'Pick a time',
    'calendar.addPhoto': 'Add photo',
    'calendar.photo': 'Photo',
    'calendar.photoCount': '{n} photos',
    'calendar.removePhotoConfirm': 'Remove this photo?',
    'calendar.uploadFailed': 'Upload failed: {message}',
    'calendar.uploadPartlyFailed': '{n} photo(s) could not be uploaded.',
    'calendar.editEvent': 'Edit event',
    'calendar.newEvent': 'New event',
    'calendar.titlePlaceholder': 'Event title',
    'calendar.begin': 'Start',
    'calendar.labelManager': 'Manage labels',
    'calendar.fromBirthdays': 'from birthday calendar',
    'calendar.labelResetTip': 'Tip: clear the name to reset the label.',
    'calendar.removeParticipant': 'Remove',
    'calendar.repeatNever': 'Never',
    'calendar.repeatDaily': 'Daily',
    'calendar.repeatWeekly': 'Weekly',
    'calendar.repeatMonthly': 'Monthly',
    'calendar.repeatYearly': 'Yearly',
    'calendar.repeatCustom': 'Custom',
    'calendar.unitDay': 'day',
    'calendar.unitWeek': 'week',
    'calendar.unitMonth': 'month',
    'calendar.unitYear': 'year',
    'calendar.unitDays': 'days',
    'calendar.unitWeeks': 'weeks',
    'calendar.unitMonths': 'months',
    'calendar.unitYears': 'years',
    'calendar.frequency': 'Frequency',
    'calendar.every': 'Every',
    'calendar.fixedDay': 'On',
    'calendar.repeatEnd': 'Ends',
    'calendar.repeatCount': 'Count',
    'calendar.repeatUntil': 'Until',
    'calendar.deleteSeriesPrompt': 'This event repeats. Choose what you want to delete.',
    'calendar.deleteOnlyThis': 'Delete only this event',
    'calendar.deleteThisAndFuture': 'Delete future events',
    'calendar.deleteAll': 'Delete all events',
    'calendar.linkSport': 'Log as sport',
    'calendar.linkSportNoSports': 'Create a sport under Fitness → Sports first.',
    'calendar.linkSportHint': 'Past occurrences are logged as workouts automatically.',
    'dashw.calendarDay': 'Day view',
    'dashw.calendarDayDesc': "Today's events",
    'dashw.calendarWeek': 'Week view',
    'dashw.calendarWeekDesc': 'The whole week at a glance',
    'dashw.calendarMonth': 'Month view',
    'dashw.calendarMonthDesc': 'Full month as a grid',
    'dashw.calendarAgenda': 'Upcoming',
    'dashw.calendarAgendaDesc': 'List of the next events',
    'dashw.birthdaysUpcoming': 'Upcoming birthdays',
    'dashw.birthdaysUpcomingDesc': "Who's next to celebrate",
    'dashw.todoOpen': 'Open tasks',
    'dashw.todoOpenDesc': 'All open tasks, tickable',
    'dashw.todoToday': 'Due today',
    'dashw.todoTodayDesc': "Just today's tasks",
    'dashw.todoCount': 'Open count',
    'dashw.todoCountDesc': 'Just the number, nice and small',
    'dashw.shoppingList': 'Shopping list',
    'dashw.shoppingListDesc': 'Open items, tickable',
    'dashw.shoppingCount': 'Open items',
    'dashw.shoppingCountDesc': 'Just the count',
    'dashw.budgetRemaining': 'Left this month',
    'dashw.budgetRemainingDesc': "What's left this month",
    'dashw.budgetExpenses': 'Expenses',
    'dashw.budgetExpensesDesc': 'Total expenses this month',
    'dashw.budgetIncome': 'Income',
    'dashw.budgetIncomeDesc': 'Total income this month',
    'dashw.budgetCategories': 'Top categories',
    'dashw.budgetCategoriesDesc': 'Where the money goes this month',
    'dashw.budgetRecent': 'Recent entries',
    'dashw.budgetRecentDesc': 'The newest income and expenses',
    'dashw.savingsBalance': 'Balance',
    'dashw.savingsBalanceDesc': 'One account or all together',
    'dashw.savingsAccounts': 'All savings accounts',
    'dashw.savingsAccountsDesc': 'Every account with its balance',
    'dashw.fitnessWeek': 'Workouts this week',
    'dashw.fitnessWeekDesc': 'Number of sessions this week',
    'dashw.fitnessRecent': 'Recent workouts',
    'dashw.fitnessRecentDesc': 'Your latest logged sessions',
    'dashw.timeWeek': 'This week',
    'dashw.timeWeekDesc': 'Hours tracked this week',
    'dashw.timeRunning': 'Running timer',
    'dashw.timeRunningDesc': 'Shows live what is running',
    'dashw.diaryRecent': 'Recent entries',
    'dashw.diaryRecentDesc': 'Your newest diary entries',
    'dashw.fuelConsumption': 'Fuel',
    'dashw.fuelConsumptionDesc': 'Last fill-up and distance',
    'dashw.tutoringOpen': 'Outstanding payments',
    'dashw.tutoringOpenDesc': 'What students still owe',
    'dashw.projectsOverview': 'Overview',
    'dashw.projectsOverviewDesc': 'Profit or hours per project',
    // Widget bodies
    'dashw.noUpcoming': 'No upcoming events.',
    'dashw.tomorrow': 'Tomorrow',
    'dashw.inDays': 'in {n} days',
    'dashw.noBirthdays': 'No birthdays saved.',
    'dashw.openTasks': 'open tasks',
    'dashw.openItems': 'open items',
    'dashw.budget.remaining': 'Left this month',
    'dashw.budget.expenses': 'Expenses',
    'dashw.budget.income': 'Income',
    'dashw.budget.uncategorized': 'Uncategorized',
    'dashw.budget.noExpenses': 'No expenses this month',
    'dashw.budget.unnamed': 'Unnamed',
    'dashw.budget.noBookings': 'No entries this month',
    'dashw.savings.total': 'Total savings',
    'dashw.savings.totalRow': 'Total',
    'dashw.savings.noAccounts': 'No savings accounts yet',
    'dashw.savings.unnamedAccount': 'Account',
    'dashw.savings.accountOne': '1 account',
    'dashw.savings.accountCount': '{count} accounts',
    'dashw.fitness.empty': 'No workouts yet',
    'dashw.fitness.weekCount': 'Workouts this week',
    'dashw.fitness.weekDuration': '{time} total',
    'dashw.timetracker.empty': 'No time tracked yet',
    'dashw.timetracker.week': 'This week',
    'dashw.timetracker.running': 'running',
    'dashw.timetracker.entriesOne': '1 entry',
    'dashw.timetracker.entriesMany': '{n} entries',
    'dashw.timetracker.noTimer': 'No timer running',
    'dashw.timetracker.untitled': 'Untitled',
    'dashw.timetracker.noCompany': 'No company',
    'dashw.timetracker.since': 'since {time}',
    'dashw.diary.empty': 'No entries yet',
    'dashw.diary.noText': 'No text',
    'dashw.fuel.error': 'Fuel data unavailable',
    'dashw.fuel.empty': 'No fuel entries yet',
    'dashw.fuel.lastFillUp': 'Last fill-up',
    'dashw.fuel.totalKm': 'Kilometres driven',
    'dashw.fuel.lastEntry': 'Last entry {date}',
    'dashw.tutoring.error': 'Tutoring data unavailable',
    'dashw.tutoring.empty': 'No students yet',
    'dashw.tutoring.open': 'Outstanding',
    'dashw.tutoring.allSettled': 'All settled',
    'dashw.tutoring.oneStudent': '1 student owes',
    'dashw.tutoring.nStudents': '{count} students owe',
    'dashw.projects.error': 'Projects unavailable',
    'dashw.projects.empty': 'No projects yet',
    'dashw.projects.missing': 'Project not found',
    'dashw.projects.timeProject': 'Time project',
    'dashw.projects.oneEntry': '1 entry',
    'dashw.projects.nEntries': '{count} entries',

    // Budget ---------------------------------------------------------------
    'budget.titleMonth': 'Budget - {month}',
    'budget.overviewYear': 'Budget Overview - {year}',
    'budget.addMonth': 'Add Month',
    'budget.addMonthToYear': 'Add New Month to {year}',
    'budget.selectMonth': 'Select Month',
    'budget.manageCategories': 'Manage Categories',
    'budget.totalIncome': 'Total Income',
    'budget.totalExpenses': 'Total Expenses',
    'budget.totalSavings': 'Total Savings',
    'budget.remainingBalance': 'Remaining Balance',
    'budget.yearlyExpensesByCategory': 'Yearly Expenses by Category',
    'budget.expensesByCategoryMonth': 'Expenses by Category - {month}',
    'budget.noExpensesYear': 'No expenses recorded this year',
    'budget.noExpensesMonth': 'No expenses recorded for {month}',
    'budget.income': 'Income',
    'budget.expenses': 'Expenses',
    'budget.savings': 'Savings',
    'budget.noIncomeYear': 'No income recorded this year',
    'budget.noIncomeMonth': 'No income recorded for {month}',
    'budget.noSavingsYear': 'No savings recorded this year',
    'budget.noSavingsMonth': 'No savings recorded for {month}',
    'budget.addIncome': 'Add Income',
    'budget.addExpense': 'Add Expense',
    'budget.addSavings': 'Add Savings',
    'budget.addToSavings': 'Add to Savings',
    'budget.unnamed': 'Unnamed',
    'budget.unknownAccount': 'Unknown Account',
    'budget.unknownCategory': 'Unknown',
    'budget.name': 'Name',
    'budget.amount': 'Amount',
    'budget.category': 'Category',
    'budget.subCategory': 'Sub-Category',
    'budget.account': 'Account',
    'budget.additionalInfo': 'Additional Info',
    'budget.additionalInfoPlaceholder': 'Additional information…',
    'budget.selectCategory': 'Select Category',
    'budget.selectSubCategory': 'Select Sub-Category (optional)',
    'budget.selectSavingsAccount': 'Select Savings Account',
    'budget.noSavingsAccounts': 'No savings accounts found. Create one in Budget → Savings.',
    'budget.addInfo': '+ Add Info',
    'budget.hideInfo': 'Hide Info',
    'budget.tabExpense': 'Expense',
    'budget.tabIncome': 'Income',
    'budget.tabSavings': 'Savings',
    'budget.addNewExpenseCategory': 'Add New Expense Category',
    'budget.addNewIncomeCategory': 'Add New Income Category',
    'budget.addNewSavingsCategory': 'Add New Savings Category',
    'budget.subCategoriesCount': '({count} sub-categories)',
    'budget.viewDetails': 'View Details',
    'budget.deleteCategory': 'Delete category',
    'budget.deleteCategoryConfirm': 'Delete "{name}" category?',
    'budget.newSubCategoryPlaceholder': 'New sub-category',
    'budget.categoryNamePlaceholder': 'Category name',
    'budget.colorLabel': 'Color:',
    'budget.addCategory': 'Add Category',
    'budget.categoryDetails': '{name} Details',
    'budget.noSubCategories': 'No sub-categories defined',
    'budget.totalCategory': 'Total Category:',
    'budget.expenseDetails': 'Expense Details',
    'budget.incomeDetails': 'Income Details',
    'budget.savingsDetails': 'Savings Details',
    'budget.editExpense': 'Edit Expense',
    'budget.editIncome': 'Edit Income',
    'budget.editSavings': 'Edit Savings',
    'budget.saveChanges': 'Save Changes',
    'budget.categorySubcategories': '{name} - Subcategories',
    'budget.pieHint': 'Click or hover over a segment to see details',
    'budget.otherSubCategory': 'Other',
    'budget.noData': 'No data available',
    'budget.total': 'Total',

    // Savings accounts -----------------------------------------------------
    'savings.title': 'Savings Accounts',
    'savings.subtitle': 'Manage your bank accounts and investments',
    'savings.transfer': 'Transfer',
    'savings.addAccount': 'Add Account',
    'savings.totalSavings': 'Total Savings',
    'savings.accountCount': 'Number of Accounts',
    'savings.budgetSavings': 'Budget Savings',
    'savings.bankAccounts': 'Bank Accounts',
    'savings.balance': 'Balance',
    'savings.addAccountTitle': 'Add Savings Account',
    'savings.accountNamePlaceholder': 'Account Name (e.g., Raiffeisen Bank)',
    'savings.accountName': 'Account Name',
    'savings.typeBank': 'Bank Account',
    'savings.typeInvestment': 'Investment',
    'savings.typeCash': 'Cash',
    'savings.typeOther': 'Other',
    'savings.initialBalance': 'Initial Balance',
    'savings.description': 'Description',
    'savings.descriptionOptional': 'Description (optional)',
    'savings.editAccount': 'Edit Account',
    'savings.saveChanges': 'Save Changes',
    'savings.transferTitle': 'Transfer Between Accounts',
    'savings.fromAccount': 'From Account',
    'savings.toAccount': 'To Account',
    'savings.amount': 'Amount',
    'savings.insufficientBalance': 'Insufficient balance for transfer',
    'savings.deleteConfirm': 'Are you sure you want to delete this account?',
    'savings.totalBalance': 'Total Balance',
    'savings.base': 'Base: {amount}',
    'savings.fromBudget': '+ {amount} from budget',
    'savings.addTransaction': 'Add Transaction',
    'savings.depositOption': 'Deposit (+)',
    'savings.withdrawalOption': 'Withdrawal (-)',
    'savings.transactionHistory': 'Transaction History',
    'savings.linkedSavings': 'Linked Savings',
    'savings.savingsEntry': 'Savings',
    'savings.deposit': 'Deposit',
    'savings.withdrawal': 'Withdrawal',
    'savings.noTransactions': 'No transactions yet. Add your first deposit or withdrawal above.',
    'savings.noLinkedSavings': 'No linked savings from budget.',
    'savings.badgeBudget': 'Budget',

    // Placeholders --------------------------------------------------------
    'placeholder.comingSoon': 'Coming soon',

    // Install hint -----------------------------------------------------------
    'install.ariaLabel': 'Install app',
    'install.title': 'Install Eulea',
    'install.tapOn': 'Tap',
    'install.inSafariChoose': 'in Safari and choose',
    'install.homeScreen': '“Add to Home Screen”',
    'install.tail': ' so Eulea works like an app (full screen + push).',
    'install.dismiss': 'Dismiss hint',

    // Time tracker -----------------------------------------------------------
    'timetracker.invalidTime': 'Invalid time',
    'timetracker.endAfterStart': 'End must be after start',
    'timetracker.deleteCompanyTitle': 'Delete company “{name}”?',
    'timetracker.deleteCompanyLinked': '{n} entries are kept but shown as “No company”.',
    'timetracker.deleteCompanyNoEntries': 'This company has no entries.',
    'timetracker.companies': 'Companies',
    'timetracker.company': 'Company',
    'timetracker.runningLabel': 'Running',
    'timetracker.companyDeleted': 'Company deleted',
    'timetracker.ready': 'Ready',
    'timetracker.tapStart': 'Tap Start',
    'timetracker.forCompany': 'for {name}',
    'timetracker.stop': 'Stop',
    'timetracker.start': 'Start',
    'timetracker.addManualTitle': 'Log time after the fact',
    'timetracker.manual': 'Manual',
    'timetracker.ofWeeklyGoal': '{pct}% of weekly goal',
    'timetracker.noEntries': 'No entries yet.',
    'timetracker.noEntriesHint': 'Press “Start” to track your first time.',
    'timetracker.week': 'Week {n}',
    'timetracker.whatDidYouDo': 'What did you work on?',
    'timetracker.duration': 'Duration: {duration}',
    'timetracker.titlePlaceholder': 'e.g. Client XY – layout',
    'timetracker.notesPlaceholder': 'Details, tickets, thoughts…',
    'timetracker.project': 'Project',
    'timetracker.noProject': 'No project',
    'timetracker.hoursPerWeek': '{n} h / week',
    'timetracker.noWeeklyGoal': 'No weekly goal',
    'timetracker.editCompany': 'Edit company',
    'timetracker.newCompany': 'New company',
    'timetracker.companyNamePlaceholder': 'Company name',
    'timetracker.weeklyHoursPlaceholder': 'Hours / week',
    'timetracker.hPerWeek': 'h / week',
    'timetracker.projectNamePlaceholder': 'e.g. Client XY',
    'timetracker.withProfitTracking': 'With profit tracking',
    'timetracker.withProfitTrackingHint': 'Expenses & income are tracked as well. Unticked, it is a time-only project.',

    // Shopping list ----------------------------------------------------------
    'shopping.unitPieces': 'piece(s)',
    'shopping.unitPackages': 'pack(s)',
    'shopping.daysAgo': '{n} days ago',
    'shopping.inDays': 'In {n} days',
    'shopping.deleteGroupConfirm': 'Delete the group and its items?',
    'shopping.listCreatedCopied': 'List "{name}" created.\n\nInvite link copied to the clipboard:\n{url}',
    'shopping.createListFailed': 'Could not create the list: {message}',
    'shopping.inviteCopied': 'Invite link copied:\n{url}',
    'shopping.inviteFailed': 'Could not create the invite link: {message}',
    'shopping.renameFailed': 'Renaming failed: {message}',
    'shopping.leaveListConfirm': 'Really leave the list "{name}"?',
    'shopping.leaveFailed': 'Leaving failed: {message}',
    'shopping.deleteListConfirm': 'Permanently delete the list "{name}" for ALL members?',
    'shopping.deleteFailed': 'Deleting failed: {message}',
    'shopping.sharedList': 'Shared list',
    'shopping.personalList': 'Personal list',
    'shopping.group': 'Group',
    'shopping.copyInvite': 'Copy invite link',
    'shopping.manageList': 'Manage list',
    'shopping.emptyHint': 'Create a group or a loose item',
    'shopping.otherPurchases': 'Other purchases',
    'shopping.groupName': 'Group name',
    'shopping.groupNamePlaceholder': 'e.g. Billa, pasta carbonara, …',
    'shopping.neededBy': 'Needed by',
    'shopping.newSharedList': 'New shared list',
    'shopping.listNamePlaceholder': 'List name (e.g. Family)',
    'shopping.createListHint': 'You will be the owner. Others can join via the invite link.',
    'shopping.deleteGroup': 'Delete group',
    'shopping.emptyGroupHint': 'No items yet — drag some here or add below',
    'shopping.extras': 'Other',
    'shopping.extrasHint': 'Items without a group — drag here or add below',
    'shopping.dragHandle': 'Move',
    'shopping.addItemPlaceholder': 'Add item…',
    'shopping.amount': 'Qty',

    // Projects ---------------------------------------------------------------
    'projects.subtitle': 'Expenses & income per project',
    'projects.emptyHint': 'No projects yet. Create your first project — e.g. “UV printer”.',
    'projects.createProject': 'Create project',
    'projects.created': 'Project created',
    'projects.deleted': 'Project deleted',
    'projects.timeBadge': 'Time',
    'projects.timeOnly': 'Time-only project',
    'projects.editProject': 'Edit project',
    'projects.newProject': 'New project',
    'projects.deleteExpenseTitle': 'Delete expense?',
    'projects.deleteIncomeTitle': 'Delete income?',
    'projects.deleteTxnMessage': '{date} · {amount} – really delete?',
    'projects.expensesIncome': 'Expenses & income',
    'projects.timeOnlyHintBefore': 'Time-only project. Log time via the',
    'projects.timeOnlyHintAfter': '; expenses & income are not tracked here.',
    'projects.topExpenses': 'Biggest expenses',
    'projects.expense': 'Expense',
    'projects.income': 'Income',
    'projects.allCount': 'All ({n})',
    'projects.emptyTxns': 'No entries yet. Add your first expense or income.',
    'projects.emptyFilter': 'No entries in this category.',
    'projects.balance': 'Balance',
    'projects.profit': 'Profit',
    'projects.inLoss': 'still in the red',
    'projects.breakEvenReached': 'Break-even reached on',
    'projects.remainingBefore': 'Still',
    'projects.remainingAfter': 'to break-even.',
    'projects.newExpense': 'New expense',
    'projects.newIncome': 'New income',
    'projects.expensePlaceholder': 'e.g. 150',
    'projects.incomePlaceholder': 'e.g. 80',
    'projects.customCategoryPlaceholder': 'or your own category',
    'projects.expenseNotePlaceholder': 'e.g. reordered cyan ink',
    'projects.incomeNotePlaceholder': 'e.g. order Lisa – 5 mugs',
    'projects.deleteProjectTitle': 'Delete project?',
    'projects.deleteProjectMessage': '“{name}” and all its entries will be deleted permanently.',
    'projects.namePlaceholder': 'e.g. UV printer',
    'projects.kind': 'Type',
    'projects.kindProfit': 'Profit & time',
    'projects.kindProfitHint': 'Expenses/income + time',
    'projects.kindTime': 'Time only',
    'projects.kindTimeHint': 'Track time only',
    'projects.icon': 'Icon',
    'projects.deleteProject': 'Delete project',
    'projects.errKindInvalid': 'Invalid type',
    'projects.errProjectMissing': 'Project is missing',

    // Fuel log ---------------------------------------------------------------
    'fuel.title': 'Fuel logs',
    'fuel.subtitle': 'Shared logs for shared cars.',
    'fuel.loadListsFailed': 'Could not load fuel logs',
    'fuel.newLog': 'New fuel log',
    'fuel.newLogMessage': 'What should the log be called? (e.g. “Car Jana & Gerhard”)',
    'fuel.logCreated': 'Fuel log created',
    'fuel.createFailed': 'Creating failed',
    'fuel.signInRequired': 'Please sign in to use fuel logs.',
    'fuel.emptyTitle': 'No fuel log yet',
    'fuel.emptyHint': 'Create a new log and send the invite link to the person you share the car with.',
    'fuel.createLog': 'Create fuel log',
    'fuel.owner': 'Owner',
    'fuel.markedPaid': 'Marked as paid',
    'fuel.undone': 'Reverted',
    'fuel.undoFailed': 'Could not revert',
    'fuel.membersOne': '1 member',
    'fuel.membersMany': '{n} members',
    'fuel.emptyEntries': 'No entries yet. Enter your current odometer reading to get started.',
    'fuel.deleteEntryMessage': 'This changes the settlement retroactively. Really delete?',
    'fuel.entrySaved': 'Entry saved',
    'fuel.currentPeriod': 'Current period',
    'fuel.settled': 'Settled',
    'fuel.you': 'you',
    'fuel.deleteEntry': 'Delete entry',
    'fuel.fillUp': 'Fill-up',
    'fuel.paidBy': 'paid by',
    'fuel.emptyPeriod': 'No entries in this period yet.',
    'fuel.noData': 'No data yet. Enter a first odometer reading.',
    'fuel.currentBalance': 'Current balance',
    'fuel.noFillUpYet': 'No fill-up yet — nothing to settle.',
    'fuel.allSettled': 'All settled 🎉',
    'fuel.undo': 'undo',
    'fuel.someone': 'Someone',
    'fuel.owes': 'owes',
    'fuel.markPaid': 'Mark as paid',
    'fuel.endGreaterThanStart': 'End km must be greater than start km',
    'fuel.endGreaterThanStartKm': 'End km must be greater than start km ({km}).',
    'fuel.firstEntryHint': 'First entry — please enter start and end km.',
    'fuel.newPeriodHint': 'New period after the fill-up.',
    'fuel.whoDrove': 'Who drove?',
    'fuel.startKm': 'Start km',
    'fuel.startKmPlaceholder': 'e.g. 124356',
    'fuel.lastFillUpAt': 'Last fill-up was at {km}.',
    'fuel.chainedStartHint': '(end of the last entry)',
    'fuel.endKm': 'End km',
    'fuel.endKmPlaceholder': 'e.g. 124500',
    'fuel.drivenPreview': '= {km} driven',
    'fuel.fillUpToggle': 'Fill-up — enter the total amount',
    'fuel.totalAmount': 'Total ({currency})',
    'fuel.costPlaceholder': 'e.g. 78.40',
    'fuel.litersOptional': 'Litres (optional)',
    'fuel.litersPlaceholder': 'e.g. 42.30',
    'fuel.notePlaceholder': 'e.g. motorway to Hamburg',
    'fuel.copyFailed': 'Could not copy the link',
    'fuel.renamed': 'Renamed',
    'fuel.deleteLogTitle': 'Delete fuel log?',
    'fuel.deleteLogMessage': '“{name}” will be deleted with all its entries. This cannot be undone.',
    'fuel.deleteForever': 'Delete permanently',
    'fuel.logDeleted': 'Log deleted',
    'fuel.leaveLogTitle': 'Leave log?',
    'fuel.leaveLogMessage': 'You will lose access to all entries.',
    'fuel.leave': 'Leave',
    'fuel.leaveFailed': 'Leaving failed',
    'fuel.inviteLink': 'Invite link',
    'fuel.inviteHint': 'Anyone who opens the link and signs in is added automatically.',
    'fuel.errOdometerMissing': 'Odometer reading is missing',
    'fuel.errBothParties': 'Both parties are required',

    // Tutoring ---------------------------------------------------------------
    'tutoring.semesterLabel': 'Term {half} {start}/{end}',
    'tutoring.parentsOweMe': 'Parents owe me',
    'tutoring.creditAvailable': 'Credit available',
    'tutoring.newStudent': 'New student',
    'tutoring.emptyStudents': 'No students yet. Add your first student to log sessions.',
    'tutoring.archived': 'archived',
    'tutoring.settled': 'settled',
    'tutoring.credit': 'Credit',
    'tutoring.hideArchived': 'Hide archived',
    'tutoring.showArchived': 'Show archived',
    'tutoring.studentCreated': 'Student created',
    'tutoring.studentNotFound': 'Student not found',
    'tutoring.deleteSessionTitle': 'Delete session?',
    'tutoring.deleteSessionMessage': 'Really delete the session on {date} ({duration})?',
    'tutoring.deletePaymentTitle': 'Delete payment?',
    'tutoring.deletePaymentMessage': 'Really delete the payment of {amount} on {date}?',
    'tutoring.activeAgain': 'Active again',
    'tutoring.archivedToast': 'Archived',
    'tutoring.changeFailed': 'Could not change',
    'tutoring.deleteStudentTitle': 'Delete student?',
    'tutoring.deleteStudentMessage': 'Really delete "{name}" with all sessions? This cannot be undone.',
    'tutoring.prevSemester': 'Previous term',
    'tutoring.nextSemester': 'Next term',
    'tutoring.hours': 'Hours',
    'tutoring.earned': 'Earned',
    'tutoring.paidDirectly': 'Paid directly',
    'tutoring.session': 'Session',
    'tutoring.payment': 'Payment',
    'tutoring.emptySemester': 'No entries in {semester}.',
    'tutoring.archive': 'Archive',
    'tutoring.deleteStudent': 'Delete student',
    'tutoring.sessionSaved': 'Session saved',
    'tutoring.paymentRecorded': 'Payment recorded',
    'tutoring.recordPayment': 'Record payment',
    'tutoring.balanced': 'Settled 🎉',
    'tutoring.creditHint': 'Will be offset against the next sessions.',
    'tutoring.owedHint': 'Parents owe you this amount.',
    'tutoring.undoDirectPayment': 'Undo direct payment',
    'tutoring.paidDirectlyHint': 'Paid directly (no credit)',
    'tutoring.togglePaid': 'Toggle paid status',
    'tutoring.paymentReceived': 'Payment received',
    'tutoring.errNameRequired': 'Name is required',
    'tutoring.editStudent': 'Edit student',
    'tutoring.namePlaceholder': 'e.g. Lukas',
    'tutoring.hourlyRate': 'Hourly rate (€/h)',
    'tutoring.ratePlaceholder': 'e.g. 25',
    'tutoring.colorAria': 'Color {color}',
    'tutoring.notesPlaceholder': 'e.g. maths grade 10, parents: Mrs Müller, 0151…',
    'tutoring.errDurationInvalid': 'Invalid duration',
    'tutoring.editSession': 'Edit session',
    'tutoring.newSession': 'New session',
    'tutoring.sessionNotePlaceholder': 'e.g. exam prep algebra',
    'tutoring.editPayment': 'Edit payment',
    'tutoring.amountPlaceholder': 'e.g. 100',
    'tutoring.currentlyOpen': 'Currently outstanding: {amount}',
    'tutoring.paymentNotePlaceholder': 'e.g. cash from mother',
    'tutoring.errPickStudent': 'Pick a student',
    'tutoring.errRateInvalid': 'Invalid hourly rate',

    // Fitness ----------------------------------------------------------------
    'fitness.deleteSportConfirm': 'Are you sure you want to delete this sport?',
    'fitness.deleteRecordConfirm': 'Delete this record?',
    'fitness.yearsOne': '{n} year',
    'fitness.yearsMany': '{n} years',
    'fitness.monthsOne': '{n} month',
    'fitness.monthsMany': '{n} months',
    'fitness.daysOne': '{n} day',
    'fitness.daysMany': '{n} days',
    'fitness.overviewTitle': 'Fitness Overview',
    'fitness.overviewSubtitle': 'Track your workouts and personal bests',
    'fitness.totalSports': 'Total Sports',
    'fitness.personalBests': 'Personal Bests',
    'fitness.activeSports': 'Active Sports',
    'fitness.thisMonth': 'This Month',
    'fitness.yourSports': 'Your Sports',
    'fitness.noSports': 'No sports added yet',
    'fitness.noSportsHint': 'Go to Sports section to add your first sport!',
    'fitness.recordsCount': '{n} records',
    'fitness.personalBest': 'Personal Best',
    'fitness.noRecords': 'No records yet',
    'fitness.sportsTitle': 'Sports & Personal Bests',
    'fitness.sportsSubtitle': 'Manage your sports and track your records',
    'fitness.addSport': 'Add Sport',
    'fitness.addFirstSport': 'Add Your First Sport',
    'fitness.startedOn': 'Started: {date}',
    'fitness.achievedOn': 'Achieved on {date}',
    'fitness.bestBadge': 'BEST',
    'fitness.backToSports': 'Back to Sports',
    'fitness.doingFor': "You've been doing this sport for",
    'fitness.totalWorkouts': 'Total Workouts',
    'fitness.personalRecords': 'Personal Records',
    'fitness.workoutStructure': 'Workout Structure',
    'fitness.addRecord': 'Add Record',
    'fitness.lifts': 'Lifts',
    'fitness.gymnastics': 'Gymnastics / Bodyweight',
    'fitness.gymnasticsShort': 'Gymnastics',
    'fitness.reps': '{n} reps',
    'fitness.workouts': 'Workouts',
    'fitness.syncedWith': 'Synced with {name}',
    'fitness.linkToCalendar': 'Link to Calendar',
    'fitness.labelFor': 'Label for {name}',
    'fitness.unlink': 'Remove link',
    'fitness.logWorkout': 'Log Workout',
    'fitness.noWorkouts': 'No workouts logged yet. Start tracking your progress!',
    'fitness.minutes': 'minutes',
    'fitness.addNewSport': 'Add New Sport',
    'fitness.selectSport': 'Select Sport',
    'fitness.startedWhen': 'When did you start this sport?',
    'fitness.editSport': 'Edit Sport',
    'fitness.sportName': 'Sport Name',
    'fitness.noStartDate': 'No start date set',
    'fitness.allRecords': 'All Records ({n})',
    'fitness.pbBadge': 'PB',
    'fitness.noRecordsHint': 'No records yet. Start tracking your {name} progress!',
    'fitness.startTime': 'Start Time',
    'fitness.durationPlaceholder': 'e.g., 60',
    'fitness.notesPlaceholder': 'How was the workout?',
    'fitness.sectionWarmup': 'Warmup',
    'fitness.sectionStrength': 'Strength',
    'fitness.sectionWorkoutOfMonth': 'Workout of Month',
    'fitness.sectionWod': 'WOD',
    'fitness.resultForTime': 'e.g., 12:34',
    'fitness.resultAmrap': 'e.g., 5+12 (rounds+reps)',
    'fitness.resultEmom': 'e.g., 10 min completed',
    'fitness.resultTabata': 'e.g., total reps',
    'fitness.resultOther': 'Your result',
    'fitness.selectExerciseEllipsis': 'Select exercise...',
    'fitness.selectExercise': 'Select Exercise',
    'fitness.detailsPlaceholder': 'e.g., 5x12',
    'fitness.workoutType': 'Workout type...',
    'fitness.wodOther': 'Other',
    'fitness.weightKg': 'Weight (kg)',
    'fitness.repsLabel': 'Reps',
    'fitness.timePlaceholder': 'Time (e.g., 2:30)',
    'fitness.deleteWorkout': 'Delete Workout',
    'fitness.structureManager': 'Workout Structure Manager',
    'fitness.exerciseNamePlaceholder': 'Exercise name (e.g., Wallballs)',
    'fitness.noExercises': 'No exercises in {section} yet. Add some above!',
    'fitness.editRecord': 'Edit Record: {name}',
    'fitness.deleteRecord': 'Delete Record',
    'fitness.addPersonalRecord': 'Add Personal Record',
    'fitness.allExercisesRecorded': 'All exercises have records! Great job!',

    // Birthday calendar ------------------------------------------------------
    'birthday.label': 'Birthday',
    'birthday.nthBirthday': '{n}{ordinal} Birthday',
    'birthday.namePlaceholder': 'e.g., Lisa',
    'birthday.birthDate': 'Birth Date',
    'birthday.day': 'Day',
    'birthday.dayPlaceholder': 'DD',
    'birthday.month': 'Month',
    'birthday.monthPlaceholder': 'MM or name',
    'birthday.year': 'Year',
    'birthday.yearPlaceholder': 'YYYY',
    'birthday.formHintBefore': 'Tab through fields. Month accepts',
    'birthday.formHintOr': 'or',
    'birthday.formHintAfter': '. Press Enter to save.',
    'birthday.tooManyDays': '{month} only has {n} days.',
    'birthday.sharedTitle': '{name} — Birthdays',
    'birthday.addPerson': 'Add Person',
    'birthday.calendarHint': 'Birthdays will appear in the calendar with a 🎁 icon and the selected color.',
    'birthday.empty': 'No birthdays added yet',
    'birthday.emptyHint': 'Click "Add Person" to get started',
    'birthday.today': 'Today!',
    'birthday.editPerson': 'Edit Person',
  },

  de: {
    // App / shell ---------------------------------------------------------
    'app.title': 'Life Manager',
    'app.tagline': 'Track, Budget & Plan',
    'app.loading': 'Lädt…',
    'app.settings': 'Einstellungen',
    'app.signOut': 'Abmelden',
    'app.syncError': 'Sync-Fehler: {message}',
    'app.closeMenu': 'Menü schließen',
    'app.joinedCalendar': 'Kalender beigetreten',
    'app.joinedCalendarNamed': 'Kalender „{name}“ beigetreten',
    'app.joinedFuelLog': 'Tank-Liste beigetreten',
    'app.joinFuelLogFailed': 'Konnte der Tank-Liste nicht beitreten: {message}',
    'app.joinedShoppingList': 'Einkaufsliste beigetreten',
    'app.joinShoppingListFailed': 'Konnte der geteilten Einkaufsliste nicht beitreten: {message}',

    // Common --------------------------------------------------------------
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.delete': 'Löschen',
    'common.edit': 'Bearbeiten',
    'common.add': 'Hinzufügen',
    'common.back': 'Zurück',
    'common.confirm': 'Bestätigen',
    'common.close': 'Schließen',
    'common.yes': 'Ja',
    'common.no': 'Nein',
    'common.loading': 'Lädt…',
    'common.uploading': 'Lädt hoch…',
    'common.search': 'Suchen',
    'common.today': 'Heute',
    'common.yesterday': 'Gestern',
    'widget.openApp': 'Eulea öffnen, um den Kalender zu laden.',
    'widget.todos': 'To-dos',
    'widget.allDone': 'Alles erledigt',
    'widget.open': 'offen',
    'widget.birthdays': 'Geburtstage',
    'widget.noBirthdays': 'Keine Geburtstage',
    'widget.budget': 'Budget',
    'widget.income': 'Einnahmen',
    'widget.expenses': 'Ausgaben',
    'widget.remaining': 'Übrig',
    'widget.timer': 'Zeiterfassung',
    'widget.timerIdle': 'Kein Timer läuft',
    'widget.start': 'Start',
    'widget.stop': 'Stopp',
    'widget.shopping': 'Einkaufsliste',
    'widget.shoppingEmpty': 'Nichts zu kaufen',
    'widget.nextUp': 'Nächster Termin',
    'widget.nothingUpcoming': 'Nichts anstehend',
    'widget.allDay': 'ganztägig',
    'widget.agenda': 'Nächste Termine',
    'widget.added': 'Hinzugefügt',
    'widget.timerStarted': 'Timer läuft.',
    'widget.timerStopped': 'Timer gestoppt.',
    'calendar.editSeriesPrompt': 'Dieser Termin wiederholt sich. Was möchtest du ändern?',
    'calendar.editOnlyThis': 'Nur diesen Termin',
    'calendar.editThisAndFuture': 'Diesen und alle zukünftigen',
    'calendar.editAll': 'Alle Termine der Serie',
    'calendar.prevMonth': 'Vorheriger Monat',
    'calendar.nextMonth': 'Nächster Monat',
    'calendar.prevYear': 'Vorheriges Jahr',
    'calendar.nextYear': 'Nächstes Jahr',
    'calendar.holidays': 'Österreichische Feiertage',
    'calendar.holidaysHint': 'Zeigt die gesetzlichen Feiertage im Kalender. Die gewählte Label-Farbe ist dann dafür reserviert, wie beim Geburtstags-Label.',
    'calendar.holidaysLabel': 'Feiertage',
    'calendar.labelColor': 'Label-Farbe',
    'common.tomorrow': 'Morgen',
    'common.upcoming': 'Bevorstehend',
    'common.all': 'Alle',
    'common.completed': 'Erledigt',
    'common.untitled': 'Ohne Titel',
    'common.optional': 'optional',
    'common.note': 'Notiz',
    'common.priority': 'Priorität',
    'common.date': 'Datum',
    'common.title': 'Titel',
    'common.name': 'Name',
    'common.new': 'Neu',
    'common.color': 'Farbe',
    'common.total': 'Gesamt',
    'common.unknown': 'Unbekannt',
    'common.notes': 'Notizen',
    'common.notesOptional': 'Notizen (optional)',
    'common.end': 'Ende',
    'common.create': 'Anlegen',
    'common.saving': 'Speichere…',
    'common.saved': 'Gespeichert',
    'common.updated': 'Aktualisiert',
    'common.deleted': 'Gelöscht',
    'common.error': 'Fehler',
    'common.saveFailed': 'Speichern fehlgeschlagen',
    'common.deleteFailed': 'Löschen fehlgeschlagen',
    'common.loadFailed': 'Laden fehlgeschlagen',
    'common.couldNotSave': 'Konnte nicht speichern',
    'common.renameFailed': 'Umbenennen fehlgeschlagen',
    'common.renameList': 'Liste umbenennen',
    'common.leaveList': 'Liste verlassen',
    'common.deleteList': 'Liste löschen',
    'common.newName': 'Neuer Name',
    'common.deleteEntryTitle': 'Eintrag löschen?',
    'common.noEntriesYet': 'Noch keine Einträge',
    'common.history': 'Verlauf',
    'common.durationMinutes': 'Dauer (Minuten)',
    'common.amountEur': 'Betrag (€)',
    'common.errAmountInvalid': 'Betrag ungültig',
    'common.errDateMissing': 'Datum fehlt',
    'common.errNameMissing': 'Name fehlt',

    // Auth ----------------------------------------------------------------
    'auth.signInTitle': 'Bei deinem Konto anmelden',
    'auth.signUpTitle': 'Neues Konto erstellen',
    'auth.email': 'E-Mail',
    'auth.password': 'Passwort',
    'auth.signIn': 'Anmelden',
    'auth.signUp': 'Registrieren',
    'auth.noAccount': 'Noch kein Konto?',
    'auth.haveAccount': 'Schon ein Konto?',
    'auth.pleaseWait': 'Bitte warten…',
    'auth.failed': 'Anmeldung fehlgeschlagen',
    'auth.confirmEmail':
      'Konto erstellt. Bestätige deine E-Mail-Adresse und melde dich dann an.',
    'onboarding.nameTitle': 'Willkommen!',
    'onboarding.nameHint': 'Wähle einen Anzeigenamen. Er wird Mitgliedern geteilter Kalender angezeigt.',
    'onboarding.continue': 'Weiter',

    // Settings ------------------------------------------------------------
    'settings.title': 'Einstellungen',
    'settings.menuOrder': 'Reihenfolge im Menü',
    'settings.menuOrderHint':
      'Verschiebe die Hauptgruppen mit den Pfeilen nach oben oder unten.',
    'settings.enabledFeatures': 'Aktivierte Funktionen',
    'settings.enabledFeaturesHint':
      'Schalte Funktionen einzeln ein oder aus. Klicke auf eine Funktion für mehr Informationen.',
    'settings.moveUp': 'Nach oben',
    'settings.moveDown': 'Nach unten',
    'settings.localFound': 'Lokale Daten gefunden',
    'settings.localFoundHint':
      'Du hast Daten aus der lokalen Version dieser App in deinem Browser. Klicke auf den Button, um sie in die Cloud zu importieren.',
    'settings.importLocal': 'Lokale Daten in Cloud importieren',
    'settings.importing': 'Importiere…',
    'settings.importSuccess': '✅ Import erfolgreich!',
    'settings.importFailed': '❌ Import fehlgeschlagen.',
    'settings.confirmMigrate':
      'Daten aus dem lokalen Browser-Speicher in die Cloud importieren? Bestehende Cloud-Daten dieses Kontos werden mit den lokalen Daten zusammengeführt (gleiche IDs werden überschrieben).',
    'settings.backup': 'Datensicherung',
    'settings.backupHint':
      'Exportiere alle deine Daten als JSON-Datei oder importiere ein vorheriges Backup.',
    'settings.exportJson': 'JSON exportieren',
    'settings.importJson': 'JSON importieren',
    'settings.language': 'Sprache',
    'settings.languageHint': 'Wähle die Sprache für die gesamte App.',
    'settings.languageEnglish': 'Englisch',
    'settings.languageGerman': 'Deutsch',
    'settings.appearance': 'Darstellung',
    'settings.appearanceHint': 'Wähle Theme und eine persönliche Akzentfarbe.',
    'settings.themeMode': 'Modus',
    'settings.themeLight': 'Hell',
    'settings.themeDark': 'Dunkel',
    'settings.themeSystem': 'System',
    'settings.primaryColor': 'Akzentfarbe',
    'settings.showPersonalCalendar': 'Persönlichen Kalender anzeigen',
    'settings.showPersonalCalendarHint': 'Blendet den persönlichen Kalender in der Seitenleiste aus (Daten bleiben erhalten).',
    'settings.sharedCalendarsVisibility': 'Geteilte Kalender',
    'settings.profile': 'Profil',
    'settings.profileHint': 'Dein Name und Avatar werden Mitgliedern geteilter Kalender angezeigt.',
    'settings.profileName': 'Anzeigename',
    'settings.profileNamePlaceholder': 'z. B. Jana',
    'settings.profileNoName': 'Kein Name gesetzt',
    'settings.profileColor': 'Avatar-Farbe',
    'settings.profileEmoji': 'Avatar',
    'settings.profileEmojiNone': 'Anfangsbuchstabe verwenden',
    'settings.profileSaved': 'Gespeichert',
    'settings.profileVisibility': 'Für Kalendermitglieder sichtbar',
    'settings.profileKindEmoji': 'Avatar',
    'settings.profileKindPhoto': 'Foto',
    'settings.profilePhotoUpload': 'Foto hochladen',
    'settings.profilePhotoReplace': 'Foto ersetzen',
    'settings.profilePhotoUploading': 'Wird hochgeladen…',
    'settings.profilePhotoRemove': 'Foto entfernen',
    'settings.profilePhotoHint': 'JPG oder PNG, max. 5 MB. Für Kalendermitglieder sichtbar.',
    'settings.profilePhotoTooLarge': 'Bild ist zu groß (max. 5 MB).',
    'sharedCalendar.createTitle': 'Geteilten Kalender erstellen',
    'sharedCalendar.namePlaceholder': 'Kalendername (z. B. Familie)',
    'sharedCalendar.create': 'Erstellen',
    'sharedCalendar.createButton': 'Geteilten Kalender erstellen',
    'sharedCalendar.shareTitle': 'Diesen Kalender teilen',
    'sharedCalendar.shareHint': 'Jeder angemeldete Nutzer, der diesen Link öffnet, wird als Mitglied hinzugefügt.',
    'sharedCalendar.copyLink': 'Link kopieren',
    'sharedCalendar.copied': 'Kopiert!',
    'sharedCalendar.delete': 'Kalender löschen',
    'sharedCalendar.deleteConfirm': 'Diesen geteilten Kalender für alle löschen? Kann nicht rückgängig gemacht werden.',
    'sharedCalendar.leave': 'Kalender verlassen',
    'sharedCalendar.leaveConfirm': 'Diesen geteilten Kalender verlassen? Du verlierst den Zugriff auf die Termine.',
    'sharedCalendar.joinSuccess': 'Geteilter Kalender beigetreten',
    'sharedCalendar.joinFailed': 'Kalender konnte nicht beigetreten werden: {message}',
    'sharedCalendar.owner': 'Besitzer',
    'sharedCalendar.member': 'Mitglied',
    'sharedCalendar.settings': 'Kalendereinstellungen',
    'sharedCalendar.membersTitle': 'Mitglieder',
    'sharedCalendar.invite': 'Einladungslink',
    'sharedCalendar.loadingMembers': 'Mitglieder werden geladen…',
    'sharedCalendar.you': 'Du',
    'diaryLock.title': 'Tagebuch schützen',
    'diaryLock.hint': 'Vor dem Öffnen des Tagebuchs nach Face ID oder einem Code fragen. Gilt für alle deine Geräte.',
    'diaryLock.enable': 'Tagebuch sperren',
    'diaryLock.enableHint': 'Sperrt sich wieder, wenn du die App verlässt, und nach 5 Minuten.',
    'diaryLock.biometrics': 'Mit Face ID entsperren',
    'diaryLock.biometricsHint': 'Face ID oder Touch ID – je nachdem, was dieses iPhone hat.',
    'diaryLock.bioUnavailable': 'Face ID ist auf diesem Gerät nicht verfügbar.',
    'diaryLock.code': 'Code',
    'diaryLock.codeSet': 'Ein Code ist gesetzt.',
    'diaryLock.codeNotSet': 'Kein Code gesetzt.',
    'diaryLock.codeRules': '4 bis 8 Ziffern.',
    'diaryLock.setCode': 'Code festlegen',
    'diaryLock.changeCode': 'Code ändern',
    'diaryLock.removeCode': 'Code entfernen',
    'diaryLock.removeCodeConfirm': 'Tagebuch-Code entfernen?',
    'diaryLock.repeatCode': 'Code wiederholen',
    'diaryLock.codeInvalid': 'Der Code muss aus 4 bis 8 Ziffern bestehen.',
    'diaryLock.codeMismatch': 'Die Codes stimmen nicht überein.',
    'diaryLock.noMethod': 'Weder Face ID noch ein Code ist eingerichtet – das Tagebuch ist noch nicht gesperrt.',
    'diaryLock.noMethodBanner': 'Die Tagebuch-Sperre ist an, aber Face ID ist hier nicht verfügbar und es ist kein Code gesetzt. Lege in den Einstellungen einen Code fest.',
    'diaryLock.lockedTitle': 'Tagebuch gesperrt',
    'diaryLock.lockedHint': 'Entsperren, um Einträge zu lesen und zu schreiben.',
    'diaryLock.unlock': 'Entsperren',
    'diaryLock.wrongCode': 'Falscher Code.',
    'diaryLock.useFaceId': 'Mit Face ID entsperren',
    'diaryLock.reason': 'Tagebuch entsperren',
    'diaryLock.bioFailed': 'Face ID hat nicht geklappt. Versuch es nochmal.',
    'diaryLock.bioFailedWithCode': 'Face ID hat nicht geklappt. Versuch es nochmal oder gib deinen Code ein.',
    'notify.title': 'Benachrichtigungen',
    'notify.digest': 'Tagesübersicht',
    'notify.digestHint': 'Eine Nachricht zur gewählten Uhrzeit mit allem, was an dem Tag im Kalender steht.',
    'notify.reminder': 'Erinnerung vor Terminen',
    'notify.off': 'Aus',
    'notify.none': 'Keine',
    'notify.minutes': '{n} Min. vorher',
    'notify.hours': '{n} Std. vorher',
    'notify.oneDay': '1 Tag vorher',
    'notify.days': '{n} Tage vorher',
    'notify.birthdayAhead': 'Geburtstags-Hinweis',
    'notify.birthdayAheadHint': 'Zur Uhrzeit der Tagesübersicht, sonst um 09:00.',
    'notify.sharedHint': 'Gilt für alle Mitglieder dieses Kalenders. Jeder kann sie unter Einstellungen → Benachrichtigungen stummschalten.',
    'notify.personalCalendar': 'Persönlicher Kalender',
    'notify.personalHint': 'Erinnerungen und Tagesübersicht für deine eigenen Termine.',
    'notify.rowReminders': 'Erinnerungen vor Terminen',
    'notify.rowDigest': 'Tagesübersicht',
    'notify.settingsTitle': 'Benachrichtigungs-Einstellungen',
    'notify.settingsHint': 'Erinnerungen, Tagesübersicht und Änderungen anderer Mitglieder – pro Kalender. Gilt für alle deine Geräte.',
    'notify.muteAll': 'Alle Push-Benachrichtigungen stummschalten',
    'notify.muteAllHint': 'Übersteuert alles unten.',
    'notify.noSharedCalendars': 'Du bist noch keinen geteilten Kalendern beigetreten.',
    'notify.mutedBadge': 'stumm',
    'notify.rowCreated': 'Neue Ereignisse',
    'notify.rowUpdated': 'Änderungen',
    'notify.rowCommented': 'Kommentare',
    'notify.rowDeleted': 'Löschungen',
    'settings.pushTitle': 'Push-Benachrichtigungen',
    'settings.pushHint': 'Erinnerungen vor Terminen, eine Tagesübersicht und Änderungen in geteilten Kalendern – auf diesem Gerät.',
    'settings.pushTest': 'Testbenachrichtigung senden',
    'settings.pushFailed': 'Fehlgeschlagen',
    'settings.pushTestSentOne': 'An 1 Gerät gesendet. Schau nach der Benachrichtigung.',
    'settings.pushTestSentMany': 'An {n} Geräte gesendet. Schau nach der Benachrichtigung.',
    'settings.pushTestNoSubs': 'Keine Anmeldung gefunden. Schalte zuerst die Benachrichtigungen ein.',
    'settings.pushTestFailed': 'Test konnte nicht gesendet werden',
    'settings.pushTestTitle': 'Eulea-Test',
    'settings.pushTestBody': 'Push-Benachrichtigungen funktionieren 🎉',
    'settings.pushUnsupported': 'Dein Browser unterstützt keine Push-Benachrichtigungen. Auf dem iPhone füge Eulea zuerst zum Home-Bildschirm hinzu – Benachrichtigungen funktionieren nur aus der installierten App.',
    'settings.pushBlockedNative': 'Mitteilungen sind für Eulea ausgeschaltet. iOS-Einstellungen → Eulea → Mitteilungen → Erlauben, dann hier einschalten.',
    'settings.pushBlockedBrowser': 'Benachrichtigungen sind in deinen Browser-Einstellungen blockiert. Erlaube sie für diese Seite und schalte sie dann hier ein.',
    'settings.profileSaveFailed': 'Speichern fehlgeschlagen',
    'settings.profileUploadFailed': 'Upload fehlgeschlagen',
    'sharedCalendar.createdBy': 'Erstellt von',
    'sharedCalendar.unknownUser': 'Unbekannter Nutzer',
    'sharedCalendar.participants': 'Teilnehmer',
    'sharedCalendar.addParticipant': 'Hinzufügen…',
    'sharedCalendar.allAdded': 'Alle Mitglieder sind dabei.',
    'sharedCalendar.withBirthdays': 'Geburtstagsliste hinzufügen',
    'sharedCalendar.withBirthdaysHint': 'Eine separate Geburtstagsliste nur für diesen Kalender. Alle Mitglieder können Einträge hinzufügen und bearbeiten.',
    'sharedCalendar.birthdays': 'Geburtstage',
    'sharedCalendar.manageBirthdays': 'Geburtstage verwalten',
    'sharedCalendar.birthdaysSection': 'Geburtstagsliste',
    'sharedCalendar.enableBirthdays': 'Geburtstagsliste aktivieren',
    'sharedCalendar.enableBirthdaysHint': 'Für alle Mitglieder dieses Kalenders sichtbar.',
    'sharedCalendar.backToCalendar': 'Zurück zum Kalender',
    'sharedCalendar.myLabel': 'Dein Label in diesem Kalender',
    'sharedCalendar.myLabelHint': 'Wird automatisch verwendet, wenn du hier ein neues Ereignis erstellst.',
    'sharedCalendar.createFailed': 'Kalender konnte nicht erstellt werden',
    'sharedCalendar.leaveFailed': 'Kalender konnte nicht verlassen werden',
    'sharedCalendar.deleteFailed': 'Kalender konnte nicht gelöscht werden',
    'color.blue': 'Blau',
    'color.lightBlue': 'Hellblau',
    'color.green': 'Grün',
    'color.pink': 'Pink',
    'color.lightPink': 'Hellpink',
    'color.yellow': 'Gelb',
    'color.orange': 'Orange',
    'color.black': 'Schwarz',
    'color.white': 'Weiß',

    // Sidebar / feature groups --------------------------------------------
    'nav.more': 'Mehr',
    'nav.allPages': 'Alle Seiten',
    'settings.account': 'Dein Konto',
    'settings.deleteAccount': 'Konto löschen',
    'settings.deletingAccount': 'Wird gelöscht…',
    'settings.deleteAccountHint':
      'Beim Löschen verschwinden dein Login und alles darin — Kalender, Budget, Tagebuch, Fotos. Das lässt sich nicht rückgängig machen.',
    'settings.deleteAccountConfirm':
      'Konto und alle Daten endgültig löschen? Das lässt sich nicht rückgängig machen.',
    'settings.deleteAccountTypeTitle': 'Zum Bestätigen LÖSCHEN eingeben',
    'settings.deleteAccountWord': 'LÖSCHEN',
    'settings.deleteAccountFailed': 'Konto konnte nicht gelöscht werden: {message}',
    'settings.tabBar': 'Tab-Leiste',
    'settings.tabBarHint': 'Wähle bis zu {n} Seiten für die Leiste am unteren Bildschirmrand. Alles andere bleibt unter „Mehr“.',
    'nav.dashboard': 'Dashboard',
    'nav.planning': 'Planung',
    'nav.calendar': 'Kalender',
    'nav.birthdays': 'Geburtstagskalender',
    'nav.todo': 'To-dos',
    'nav.reminders': 'Erinnerungen',
    'nav.finance': 'Finanzen',
    'nav.budget': 'Budget',
    'nav.savings': 'Sparen',
    'nav.fitness': 'Fitness',
    'nav.fitnessOverview': 'Übersicht',
    'nav.sports': 'Sportarten',
    'nav.personal': 'Persönlich',
    'nav.diary': 'Tagebuch',
    'nav.habits': 'Gewohnheiten',
    'nav.work': 'Arbeit',
    'nav.timetracker': 'Zeiterfassung',
    'nav.shopping': 'Einkaufsliste',
    'nav.fuel': 'Tank-Liste',
    'nav.tutoring': 'Nachhilfe',
    'nav.projects': 'Projekte',

    'navDesc.dashboard':
      'Übersichtliche Wochenansicht mit allen anstehenden Terminen, Geburtstagen und einem schnellen Blick auf deine wichtigsten Daten.',
    'navDesc.planning':
      'Alles rund um Planung: Kalender, Geburtstage, Aufgaben und Erinnerungen.',
    'navDesc.calendar': 'Monatsansicht mit Events, farbigen Labels und Wiederholungen.',
    'navDesc.birthdays':
      'Verwalte Geburtstage separat und synchronisiere sie mit dem Hauptkalender.',
    'navDesc.todo': 'Einfache To-do-Liste für deine Aufgaben.',
    'navDesc.reminders': 'Erinnerungen für wichtige Dinge.',
    'navDesc.finance': 'Alles rund um Geld: Budget, Sparen und Konten.',
    'navDesc.budget': 'Einnahmen und Ausgaben dieses Monats und Trend.',
    'navDesc.savings': 'Sparziele, Konten und Sparquoten verfolgen.',
    'navDesc.fitness':
      'Tracke Workouts, persönliche Bestleistungen und plane wiederkehrende Trainings über den Kalender.',
    'navDesc.fitnessOverview': 'Aktive Sportarten und Statistiken.',
    'navDesc.sports':
      'Detail-Ansichten je Sportart inkl. Workouts, PRs und Workout-Strukturen.',
    'navDesc.personal': 'Persönliches: Tagebuch, Gewohnheiten und mehr.',
    'navDesc.diary': 'Persönliches Tagebuch.',
    'navDesc.habits': 'Gewohnheiten verfolgen und Streaks aufbauen.',
    'navDesc.work': 'Tools für die Arbeit: Zeiterfassung und mehr.',
    'navDesc.timetracker': 'Arbeitszeit erfassen.',
    'navDesc.shopping': 'Einkaufslisten mit Rezepten und Mengenberechnung.',
    'navDesc.fuel': 'Geteilte Tank-Liste: Kilometerstände festhalten und Tankkosten nach gefahrenen Kilometern aufteilen.',
    'navDesc.tutoring': 'Nachhilfe-Sitzungen pro Schüler und Semester erfassen. Bezahlt-Status pro Sitzung abhaken.',
    'navDesc.projects': 'Pro Projekt Ausgaben & Einnahmen erfassen, kumulative Bilanz und Break-even sehen.',

    // Diary ---------------------------------------------------------------
    'diary.title': 'Tagebuch',
    'diary.subtitle': 'Persönliches Tagebuch',
    'diary.newEntry': 'Neuer Eintrag',
    'diary.firstEntry': 'Ersten Eintrag erstellen',
    'diary.empty': 'Noch keine Einträge. Beginne dein Tagebuch!',
    'diary.titlePlaceholder': 'Titel…',
    'diary.bodyPlaceholder': 'Schreib hier deinen Eintrag…',
    'diary.addPhoto': 'Foto hinzufügen',
    'diary.removePhoto': 'Foto entfernen',
    'diary.deleteConfirm': 'Eintrag löschen?',
    'diary.morePhotos': '+{count} weitere Fotos',
    'diary.uploadFailed': 'Foto-Upload fehlgeschlagen: {message}',
    'diary.showTitleInList': 'Titel in der Liste anzeigen',
    'diary.showTitleInListHint': 'Wenn aus, zeigt die Liste für diesen Eintrag nur das Datum.',

    // Todo ----------------------------------------------------------------
    'todo.title': 'To-dos',
    'todo.subtitle': 'To-dos verwalten',
    'todo.newTaskPlaceholder': 'Was muss erledigt werden?',
    'todo.notePlaceholder': 'Zusätzliche Infos…',
    'todo.allGroups': 'Alle Gruppen',
    'todo.newGroup': 'Neue Gruppe',
    'todo.newGroupShort': 'Neu',
    'todo.manageGroups': 'Gruppen verwalten',
    'todo.groupNamePrompt': 'Name der neuen Gruppe',
    'todo.priorityHigh': 'Hoch',
    'todo.priorityMedium': 'Mittel',
    'todo.priorityLow': 'Niedrig',
    'todo.viewToday': 'Heute',
    'todo.viewUpcoming': 'Demnächst',
    'todo.viewAll': 'Alle',
    'todo.viewCompleted': 'Erledigt',
    'todo.editTitle': 'Aufgabe bearbeiten',
    'todo.newTitle': 'Neue Aufgabe',
    'todo.dateOptional': 'Datum (optional)',
    'todo.priorityOptional': 'Priorität (optional)',
    'todo.groupOptional': 'Gruppe (optional)',
    'todo.noteOptional': 'Notiz (optional)',
    'todo.noGroup': 'Keine',
    'todo.markDone': 'Erledigt',
    'todo.markOpen': 'Als offen markieren',
    'todo.overdue': 'überfällig',
    'todo.emptyToday': 'Keine Aufgaben für heute.',
    'todo.emptyUpcoming': 'Keine bevorstehenden Aufgaben.',
    'todo.emptyAll': 'Noch keine Aufgaben.',
    'todo.emptyCompleted': 'Noch nichts erledigt.',
    'todo.noGroups': 'Noch keine Gruppen.',
    'todo.noDate': 'Ohne Datum',
    'todo.tasksOne': 'Aufgabe',
    'todo.tasksOther': 'Aufgaben',
    'todo.confirmDelete': 'Aufgabe löschen?',
    'todo.deleteGroupConfirm':
      'Gruppe löschen? Die Aufgaben bleiben erhalten (ohne Gruppe).',
    'todo.timeOptional': 'Uhrzeit (optional)',
    'todo.showInCalendar': 'Im Kalender anzeigen',
    'todo.labelColor': 'Label-Farbe',
    'todo.calendarTarget': 'Kalender',
    'todo.personalCalendar': 'Persönlicher Kalender',
    'todo.inUse': 'belegt',
    'todo.calendarHint':
      'Aufgaben mit Datum erscheinen im Kalender mit Aufgaben-Symbol in der gewählten Farbe; erledigte werden durchgestrichen.',
    'todo.calendarSharedHint':
      'Mitglieder dieses Kalenders sehen diese Aufgaben.',

    // Dashboard widgets -----------------------------------------------------
    'dashboard.subtitle': 'Willkommen zurück! Dein Überblick.',
    'dashboard.customize': 'Anpassen',
    'dashboard.doneEditing': 'Fertig',
    'dashboard.reset': 'Layout zurücksetzen',
    'dashboard.editHint': 'Widgets am Kopf ziehen, unten rechts in der Größe ändern, mit × entfernen.',
    'dashboard.allDay': 'ganztägig',
    'dashboard.emptyDay': 'Heute nichts geplant.',
    'dashboard.emptyTodos': 'Keine offenen Aufgaben.',
    'dashboard.emptyShopping': 'Einkaufsliste ist leer.',
    // Galerie
    'dashw.widget': 'Widget',
    'dashw.galleryTitle': 'Widget hinzufügen',
    'dashw.searchWidgets': 'In Widgets suchen',
    'dashw.noResults': 'Nichts gefunden.',
    'dashw.styleCount': '{n} Stile',
    'dashw.pickInstance': 'Welche(n)?',
    'dashw.pickSize': 'Größe',
    'dashw.resizeHint': 'Die Größe lässt sich später jederzeit ändern.',
    'dashw.addWidget': 'Widget hinzufügen',
    'dashw.removeWidget': 'Widget entfernen',
    'dashw.emptyDashboard': 'Noch keine Widgets auf dem Dashboard.',
    'dashw.resetConfirm': 'Dashboard auf die Standard-Widgets zurücksetzen?',
    'dashw.sizeSmall': 'Klein',
    'dashw.sizeMedium': 'Mittel',
    'dashw.sizeLarge': 'Groß',
    'dashw.myList': 'Meine Liste',
    'dashw.allAccounts': 'Alle Konten',
    'dashw.allSports': 'Alle Sportarten',
    // Katalog: Stile + Beschreibungen
    'calendar.suggestLast': 'Zuletzt {date}',
    'calendar.editingSeries': 'Serie wird bearbeitet',
    'calendar.clickedOn': 'Geklickt auf',
    'calendar.seriesStart': 'Serien‑Start',
    'calendar.todosOf': '{name}s To-dos',
    'calendar.fromTodosOf': 'aus To-dos ({name})',
    'calendar.fromTodos': 'aus To-dos',
    'calendar.activity': 'Aktivität',
    'calendar.noActivity': 'Noch keine Aktivität.',
    'calendar.noActivityHint': 'Änderungen und Kommentare in geteilten Kalendern erscheinen hier.',
    'calendar.eventFallback': 'Ereignis',
    'calendar.fieldLabel': 'Label',
    'calendar.recurrence': 'Wiederholung',
    'calendar.auditCreated': 'Ereignis erstellt',
    'calendar.auditUpdated': 'Ereignis aktualisiert',
    'calendar.auditFieldsUpdated': '{fields} aktualisiert',
    'calendar.deleteEventConfirm': 'Event löschen?',
    'calendar.allDay': 'Ganztägig',
    'calendar.event': 'Event',
    'calendar.labels': 'Labels',
    'calendar.manageLabels': 'Labels verwalten',
    'calendar.nothingPlanned': 'Nichts geplant.',
    'calendar.weekShort': 'KW',
    'calendar.moreCount': '+{n} mehr',
    'calendar.calendarView': 'Kalenderansicht',
    'calendar.loadSettingsFailed': 'Kalendereinstellungen konnten nicht geladen werden',
    'calendar.commentPlaceholder': 'Kommentieren…',
    'calendar.send': 'Senden',
    'calendar.attachments': 'Anhänge',
    'calendar.auditDeleted': 'Ereignis gelöscht',
    'calendar.typeTime': 'Uhrzeit eintippen',
    'calendar.collapseHeader': 'Details einklappen',
    'calendar.expandHeader': 'Details anzeigen',
    'calendar.pickTime': 'Uhrzeit wählen',
    'calendar.addPhoto': 'Foto hinzufügen',
    'calendar.photo': 'Foto',
    'calendar.photoCount': '{n} Fotos',
    'calendar.removePhotoConfirm': 'Dieses Foto entfernen?',
    'calendar.uploadFailed': 'Upload fehlgeschlagen: {message}',
    'calendar.uploadPartlyFailed': '{n} Foto(s) konnten nicht hochgeladen werden.',
    'calendar.editEvent': 'Event bearbeiten',
    'calendar.newEvent': 'Neues Event',
    'calendar.titlePlaceholder': 'Event Titel',
    'calendar.begin': 'Beginn',
    'calendar.labelManager': 'Label-Verwaltung',
    'calendar.fromBirthdays': 'aus Geburtstagskalender',
    'calendar.labelResetTip': 'Tipp: Leere den Namen, um das Label zurückzusetzen.',
    'calendar.removeParticipant': 'Entfernen',
    'calendar.repeatNever': 'Nie',
    'calendar.repeatDaily': 'Täglich',
    'calendar.repeatWeekly': 'Wöchentlich',
    'calendar.repeatMonthly': 'Monatlich',
    'calendar.repeatYearly': 'Jährlich',
    'calendar.repeatCustom': 'Eigene',
    'calendar.unitDay': 'Tag',
    'calendar.unitWeek': 'Woche',
    'calendar.unitMonth': 'Monat',
    'calendar.unitYear': 'Jahr',
    'calendar.unitDays': 'Tage',
    'calendar.unitWeeks': 'Wochen',
    'calendar.unitMonths': 'Monate',
    'calendar.unitYears': 'Jahre',
    'calendar.frequency': 'Häufigkeit',
    'calendar.every': 'Jeden',
    'calendar.fixedDay': 'Festgelegter Tag',
    'calendar.repeatEnd': 'Ende der Wiederholung',
    'calendar.repeatCount': 'Anzahl',
    'calendar.repeatUntil': 'Bis',
    'calendar.deleteSeriesPrompt': 'Dieser Termin wiederholt sich. Bitte wählen Sie aus, was Sie löschen möchten.',
    'calendar.deleteOnlyThis': 'Nur diesen Termin löschen',
    'calendar.deleteThisAndFuture': 'Zukünftige Termine löschen',
    'calendar.deleteAll': 'Alle Termine löschen',
    'calendar.linkSport': 'Als Sport eintragen',
    'calendar.linkSportNoSports': 'Lege zuerst eine Sportart unter Fitness → Sports an.',
    'calendar.linkSportHint': 'Vergangene Wiederholungen werden automatisch als Workouts angelegt.',
    'dashw.calendarDay': 'Tagesansicht',
    'dashw.calendarDayDesc': 'Termine von heute',
    'dashw.calendarWeek': 'Wochenansicht',
    'dashw.calendarWeekDesc': 'Die ganze Woche auf einen Blick',
    'dashw.calendarMonth': 'Monatsübersicht',
    'dashw.calendarMonthDesc': 'Ganzer Monat als Raster',
    'dashw.calendarAgenda': 'Nächste Termine',
    'dashw.calendarAgendaDesc': 'Liste der kommenden Termine',
    'dashw.birthdaysUpcoming': 'Nächste Geburtstage',
    'dashw.birthdaysUpcomingDesc': 'Wer als Nächstes Geburtstag hat',
    'dashw.todoOpen': 'Offene Aufgaben',
    'dashw.todoOpenDesc': 'Alle offenen Aufgaben, abhakbar',
    'dashw.todoToday': 'Heute fällig',
    'dashw.todoTodayDesc': 'Nur die Aufgaben von heute',
    'dashw.todoCount': 'Anzahl offen',
    'dashw.todoCountDesc': 'Nur die Zahl, ganz klein',
    'dashw.shoppingList': 'Einkaufsliste',
    'dashw.shoppingListDesc': 'Offene Artikel, abhakbar',
    'dashw.shoppingCount': 'Offene Artikel',
    'dashw.shoppingCountDesc': 'Nur die Anzahl',
    'dashw.budgetRemaining': 'Übrig diesen Monat',
    'dashw.budgetRemainingDesc': 'Was diesen Monat noch übrig ist',
    'dashw.budgetExpenses': 'Ausgaben',
    'dashw.budgetExpensesDesc': 'Summe der Ausgaben diesen Monat',
    'dashw.budgetIncome': 'Einnahmen',
    'dashw.budgetIncomeDesc': 'Summe der Einnahmen diesen Monat',
    'dashw.budgetCategories': 'Top-Kategorien',
    'dashw.budgetCategoriesDesc': 'Wofür das Geld diesen Monat weggeht',
    'dashw.budgetRecent': 'Letzte Buchungen',
    'dashw.budgetRecentDesc': 'Die neuesten Einnahmen und Ausgaben',
    'dashw.savingsBalance': 'Kontostand',
    'dashw.savingsBalanceDesc': 'Stand eines Kontos oder aller zusammen',
    'dashw.savingsAccounts': 'Alle Sparkonten',
    'dashw.savingsAccountsDesc': 'Liste aller Konten mit Stand',
    'dashw.fitnessWeek': 'Workouts diese Woche',
    'dashw.fitnessWeekDesc': 'Anzahl Trainings diese Woche',
    'dashw.fitnessRecent': 'Letzte Workouts',
    'dashw.fitnessRecentDesc': 'Die zuletzt eingetragenen Trainings',
    'dashw.timeWeek': 'Diese Woche',
    'dashw.timeWeekDesc': 'Erfasste Stunden diese Woche',
    'dashw.timeRunning': 'Laufender Timer',
    'dashw.timeRunningDesc': 'Zeigt live, was gerade läuft',
    'dashw.diaryRecent': 'Letzte Einträge',
    'dashw.diaryRecentDesc': 'Die neuesten Tagebucheinträge',
    'dashw.fuelConsumption': 'Tanken',
    'dashw.fuelConsumptionDesc': 'Letzter Tankstopp und Kilometer',
    'dashw.tutoringOpen': 'Offene Zahlungen',
    'dashw.tutoringOpenDesc': 'Was Schüler noch schulden',
    'dashw.projectsOverview': 'Übersicht',
    'dashw.projectsOverviewDesc': 'Gewinn bzw. Stunden je Projekt',
    // Widget-Inhalte
    'dashw.noUpcoming': 'Keine kommenden Termine.',
    'dashw.tomorrow': 'Morgen',
    'dashw.inDays': 'in {n} Tagen',
    'dashw.noBirthdays': 'Keine Geburtstage eingetragen.',
    'dashw.openTasks': 'offene Aufgaben',
    'dashw.openItems': 'offene Artikel',
    'dashw.budget.remaining': 'Übrig diesen Monat',
    'dashw.budget.expenses': 'Ausgaben',
    'dashw.budget.income': 'Einnahmen',
    'dashw.budget.uncategorized': 'Ohne Kategorie',
    'dashw.budget.noExpenses': 'Keine Ausgaben in diesem Monat',
    'dashw.budget.unnamed': 'Ohne Namen',
    'dashw.budget.noBookings': 'Keine Buchungen in diesem Monat',
    'dashw.savings.total': 'Sparen gesamt',
    'dashw.savings.totalRow': 'Gesamt',
    'dashw.savings.noAccounts': 'Noch keine Sparkonten',
    'dashw.savings.unnamedAccount': 'Konto',
    'dashw.savings.accountOne': '1 Konto',
    'dashw.savings.accountCount': '{count} Konten',
    'dashw.fitness.empty': 'Noch keine Workouts',
    'dashw.fitness.weekCount': 'Workouts diese Woche',
    'dashw.fitness.weekDuration': '{time} gesamt',
    'dashw.timetracker.empty': 'Noch keine Zeiten erfasst',
    'dashw.timetracker.week': 'Diese Woche',
    'dashw.timetracker.running': 'läuft',
    'dashw.timetracker.entriesOne': '1 Eintrag',
    'dashw.timetracker.entriesMany': '{n} Einträge',
    'dashw.timetracker.noTimer': 'Kein Timer aktiv',
    'dashw.timetracker.untitled': 'Ohne Titel',
    'dashw.timetracker.noCompany': 'Ohne Firma',
    'dashw.timetracker.since': 'seit {time}',
    'dashw.diary.empty': 'Noch keine Einträge',
    'dashw.diary.noText': 'Kein Text',
    'dashw.fuel.error': 'Tankdaten nicht geladen',
    'dashw.fuel.empty': 'Noch keine Tankeinträge',
    'dashw.fuel.lastFillUp': 'Letzter Tankstopp',
    'dashw.fuel.totalKm': 'Gefahrene km',
    'dashw.fuel.lastEntry': 'Letzter Eintrag {date}',
    'dashw.tutoring.error': 'Nachhilfe nicht geladen',
    'dashw.tutoring.empty': 'Noch keine Schüler',
    'dashw.tutoring.open': 'Offene Beträge',
    'dashw.tutoring.allSettled': 'Alles bezahlt',
    'dashw.tutoring.oneStudent': '1 Schüler offen',
    'dashw.tutoring.nStudents': '{count} Schüler offen',
    'dashw.projects.error': 'Projekte nicht geladen',
    'dashw.projects.empty': 'Noch keine Projekte',
    'dashw.projects.missing': 'Projekt nicht gefunden',
    'dashw.projects.timeProject': 'Zeit-Projekt',
    'dashw.projects.oneEntry': '1 Eintrag',
    'dashw.projects.nEntries': '{count} Einträge',

    // Budget ---------------------------------------------------------------
    'budget.titleMonth': 'Budget - {month}',
    'budget.overviewYear': 'Budget-Übersicht - {year}',
    'budget.addMonth': 'Monat hinzufügen',
    'budget.addMonthToYear': 'Neuen Monat zu {year} hinzufügen',
    'budget.selectMonth': 'Monat wählen',
    'budget.manageCategories': 'Kategorien verwalten',
    'budget.totalIncome': 'Einnahmen gesamt',
    'budget.totalExpenses': 'Ausgaben gesamt',
    'budget.totalSavings': 'Sparen gesamt',
    'budget.remainingBalance': 'Verbleibend',
    'budget.yearlyExpensesByCategory': 'Jahresausgaben nach Kategorie',
    'budget.expensesByCategoryMonth': 'Ausgaben nach Kategorie - {month}',
    'budget.noExpensesYear': 'Keine Ausgaben in diesem Jahr erfasst',
    'budget.noExpensesMonth': 'Keine Ausgaben für {month} erfasst',
    'budget.income': 'Einnahmen',
    'budget.expenses': 'Ausgaben',
    'budget.savings': 'Sparen',
    'budget.noIncomeYear': 'Keine Einnahmen in diesem Jahr erfasst',
    'budget.noIncomeMonth': 'Keine Einnahmen für {month} erfasst',
    'budget.noSavingsYear': 'Keine Sparbeträge in diesem Jahr erfasst',
    'budget.noSavingsMonth': 'Keine Sparbeträge für {month} erfasst',
    'budget.addIncome': 'Einnahme hinzufügen',
    'budget.addExpense': 'Ausgabe hinzufügen',
    'budget.addSavings': 'Sparbetrag hinzufügen',
    'budget.addToSavings': 'Zum Sparen hinzufügen',
    'budget.unnamed': 'Ohne Namen',
    'budget.unknownAccount': 'Unbekanntes Konto',
    'budget.unknownCategory': 'Unbekannt',
    'budget.name': 'Name',
    'budget.amount': 'Betrag',
    'budget.category': 'Kategorie',
    'budget.subCategory': 'Unterkategorie',
    'budget.account': 'Konto',
    'budget.additionalInfo': 'Zusätzliche Info',
    'budget.additionalInfoPlaceholder': 'Zusätzliche Informationen…',
    'budget.selectCategory': 'Kategorie wählen',
    'budget.selectSubCategory': 'Unterkategorie wählen (optional)',
    'budget.selectSavingsAccount': 'Sparkonto wählen',
    'budget.noSavingsAccounts': 'Keine Sparkonten gefunden. Lege eins unter Budget → Sparen an.',
    'budget.addInfo': '+ Info hinzufügen',
    'budget.hideInfo': 'Info ausblenden',
    'budget.tabExpense': 'Ausgaben',
    'budget.tabIncome': 'Einnahmen',
    'budget.tabSavings': 'Sparen',
    'budget.addNewExpenseCategory': 'Neue Ausgaben-Kategorie',
    'budget.addNewIncomeCategory': 'Neue Einnahmen-Kategorie',
    'budget.addNewSavingsCategory': 'Neue Spar-Kategorie',
    'budget.subCategoriesCount': '({count} Unterkategorien)',
    'budget.viewDetails': 'Details ansehen',
    'budget.deleteCategory': 'Kategorie löschen',
    'budget.deleteCategoryConfirm': 'Kategorie „{name}“ löschen?',
    'budget.newSubCategoryPlaceholder': 'Neue Unterkategorie',
    'budget.categoryNamePlaceholder': 'Name der Kategorie',
    'budget.colorLabel': 'Farbe:',
    'budget.addCategory': 'Kategorie hinzufügen',
    'budget.categoryDetails': '{name} – Details',
    'budget.noSubCategories': 'Keine Unterkategorien definiert',
    'budget.totalCategory': 'Kategorie gesamt:',
    'budget.expenseDetails': 'Details zur Ausgabe',
    'budget.incomeDetails': 'Details zur Einnahme',
    'budget.savingsDetails': 'Details zum Sparbetrag',
    'budget.editExpense': 'Ausgabe bearbeiten',
    'budget.editIncome': 'Einnahme bearbeiten',
    'budget.editSavings': 'Sparbetrag bearbeiten',
    'budget.saveChanges': 'Änderungen speichern',
    'budget.categorySubcategories': '{name} - Unterkategorien',
    'budget.pieHint': 'Klicke auf ein Segment oder fahre darüber, um Details zu sehen',
    'budget.otherSubCategory': 'Sonstiges',
    'budget.noData': 'Keine Daten vorhanden',
    'budget.total': 'Gesamt',

    // Sparkonten -----------------------------------------------------------
    'savings.title': 'Sparkonten',
    'savings.subtitle': 'Verwalte deine Bankkonten und Anlagen',
    'savings.transfer': 'Umbuchen',
    'savings.addAccount': 'Konto hinzufügen',
    'savings.totalSavings': 'Sparen gesamt',
    'savings.accountCount': 'Anzahl Konten',
    'savings.budgetSavings': 'Sparen aus Budget',
    'savings.bankAccounts': 'Bankkonten',
    'savings.balance': 'Kontostand',
    'savings.addAccountTitle': 'Sparkonto hinzufügen',
    'savings.accountNamePlaceholder': 'Kontoname (z. B. Raiffeisen Bank)',
    'savings.accountName': 'Kontoname',
    'savings.typeBank': 'Bankkonto',
    'savings.typeInvestment': 'Anlage',
    'savings.typeCash': 'Bargeld',
    'savings.typeOther': 'Sonstiges',
    'savings.initialBalance': 'Anfangsbestand',
    'savings.description': 'Beschreibung',
    'savings.descriptionOptional': 'Beschreibung (optional)',
    'savings.editAccount': 'Konto bearbeiten',
    'savings.saveChanges': 'Änderungen speichern',
    'savings.transferTitle': 'Umbuchung zwischen Konten',
    'savings.fromAccount': 'Von Konto',
    'savings.toAccount': 'Auf Konto',
    'savings.amount': 'Betrag',
    'savings.insufficientBalance': 'Nicht genug Guthaben für die Umbuchung',
    'savings.deleteConfirm': 'Möchtest du dieses Konto wirklich löschen?',
    'savings.totalBalance': 'Gesamtstand',
    'savings.base': 'Basis: {amount}',
    'savings.fromBudget': '+ {amount} aus dem Budget',
    'savings.addTransaction': 'Buchung hinzufügen',
    'savings.depositOption': 'Einzahlung (+)',
    'savings.withdrawalOption': 'Auszahlung (-)',
    'savings.transactionHistory': 'Buchungsverlauf',
    'savings.linkedSavings': 'Verknüpfte Sparbeträge',
    'savings.savingsEntry': 'Sparen',
    'savings.deposit': 'Einzahlung',
    'savings.withdrawal': 'Auszahlung',
    'savings.noTransactions': 'Noch keine Buchungen. Trage oben deine erste Ein- oder Auszahlung ein.',
    'savings.noLinkedSavings': 'Keine verknüpften Sparbeträge aus dem Budget.',
    'savings.badgeBudget': 'Budget',

    // Placeholders --------------------------------------------------------
    'placeholder.comingSoon': 'Demnächst verfügbar',

    // Installations-Hinweis --------------------------------------------------
    'install.ariaLabel': 'App installieren',
    'install.title': 'Eulea installieren',
    'install.tapOn': 'Tippe auf',
    'install.inSafariChoose': 'in Safari und wähle',
    'install.homeScreen': '„Zum Home-Bildschirm"',
    'install.tail': ', damit Eulea wie eine App funktioniert (Vollbild + Push).',
    'install.dismiss': 'Hinweis schließen',

    // Zeiterfassung ----------------------------------------------------------
    'timetracker.invalidTime': 'Zeit ungültig',
    'timetracker.endAfterStart': 'Ende muss nach dem Start liegen',
    'timetracker.deleteCompanyTitle': 'Firma „{name}" löschen?',
    'timetracker.deleteCompanyLinked': '{n} Einträge bleiben erhalten, werden aber als „Ohne Firma" gezeigt.',
    'timetracker.deleteCompanyNoEntries': 'Diese Firma hat keine Einträge.',
    'timetracker.companies': 'Firmen',
    'timetracker.company': 'Firma',
    'timetracker.runningLabel': 'Läuft',
    'timetracker.companyDeleted': 'Firma gelöscht',
    'timetracker.ready': 'Bereit',
    'timetracker.tapStart': 'Tippe auf Start',
    'timetracker.forCompany': 'für {name}',
    'timetracker.stop': 'Stoppen',
    'timetracker.start': 'Starten',
    'timetracker.addManualTitle': 'Zeit nachtragen',
    'timetracker.manual': 'Manuell',
    'timetracker.ofWeeklyGoal': '{pct}% des Wochenziels',
    'timetracker.noEntries': 'Noch keine Einträge.',
    'timetracker.noEntriesHint': 'Drücke „Starten" um die erste Zeit zu erfassen.',
    'timetracker.week': 'KW {n}',
    'timetracker.whatDidYouDo': 'Was hast du gemacht?',
    'timetracker.duration': 'Dauer: {duration}',
    'timetracker.titlePlaceholder': 'z.B. Kunde XY – Layout',
    'timetracker.notesPlaceholder': 'Details, Tickets, Gedanken…',
    'timetracker.project': 'Projekt',
    'timetracker.noProject': 'Ohne Projekt',
    'timetracker.hoursPerWeek': '{n} h / Woche',
    'timetracker.noWeeklyGoal': 'Kein Wochenziel',
    'timetracker.editCompany': 'Firma bearbeiten',
    'timetracker.newCompany': 'Neue Firma',
    'timetracker.companyNamePlaceholder': 'Name der Firma',
    'timetracker.weeklyHoursPlaceholder': 'Stunden / Woche',
    'timetracker.hPerWeek': 'h / Woche',
    'timetracker.projectNamePlaceholder': 'z.B. Kunde XY',
    'timetracker.withProfitTracking': 'Mit Gewinn-Tracking',
    'timetracker.withProfitTrackingHint': 'Ausgaben & Einnahmen werden zusätzlich erfasst. Ohne Häkchen ist es ein reines Zeit-Projekt.',

    // Einkaufsliste ----------------------------------------------------------
    'shopping.unitPieces': 'Stück(e)',
    'shopping.unitPackages': 'Packung(en)',
    'shopping.daysAgo': 'Vor {n} Tagen',
    'shopping.inDays': 'In {n} Tagen',
    'shopping.deleteGroupConfirm': 'Gruppe und zugehörige Items löschen?',
    'shopping.listCreatedCopied': 'Liste "{name}" wurde erstellt.\n\nEinladungslink wurde in die Zwischenablage kopiert:\n{url}',
    'shopping.createListFailed': 'Liste konnte nicht erstellt werden: {message}',
    'shopping.inviteCopied': 'Einladungslink kopiert:\n{url}',
    'shopping.inviteFailed': 'Konnte Einladungslink nicht erstellen: {message}',
    'shopping.renameFailed': 'Umbenennen fehlgeschlagen: {message}',
    'shopping.leaveListConfirm': 'Liste "{name}" wirklich verlassen?',
    'shopping.leaveFailed': 'Verlassen fehlgeschlagen: {message}',
    'shopping.deleteListConfirm': 'Liste "{name}" für ALLE Mitglieder unwiderruflich löschen?',
    'shopping.deleteFailed': 'Löschen fehlgeschlagen: {message}',
    'shopping.sharedList': 'Geteilte Liste',
    'shopping.personalList': 'Persönliche Liste',
    'shopping.group': 'Gruppe',
    'shopping.copyInvite': 'Einladungslink kopieren',
    'shopping.manageList': 'Liste verwalten',
    'shopping.emptyHint': 'Erstelle eine Gruppe oder ein freies Item',
    'shopping.otherPurchases': 'Weitere Einkäufe',
    'shopping.groupName': 'Gruppenname',
    'shopping.groupNamePlaceholder': 'z.B. Billa, Pasta Carbonara, …',
    'shopping.neededBy': 'Benötigt bis',
    'shopping.newSharedList': 'Neue geteilte Liste',
    'shopping.listNamePlaceholder': 'Name der Liste (z.B. Familie)',
    'shopping.createListHint': 'Du wirst Eigentümer:in. Andere können per Einladungslink beitreten.',
    'shopping.deleteGroup': 'Gruppe löschen',
    'shopping.emptyGroupHint': 'Noch keine Items — hierher ziehen oder unten hinzufügen',
    'shopping.extras': 'Weitere',
    'shopping.extrasHint': 'Items ohne Gruppe — hierher ziehen oder unten hinzufügen',
    'shopping.dragHandle': 'Verschieben',
    'shopping.addItemPlaceholder': 'Item hinzufügen…',
    'shopping.amount': 'Menge',

    // Projekte ---------------------------------------------------------------
    'projects.subtitle': 'Ausgaben & Einnahmen pro Projekt',
    'projects.emptyHint': 'Noch keine Projekte. Lege dein erstes Projekt an — z.B. „UV-Drucker".',
    'projects.createProject': 'Projekt anlegen',
    'projects.created': 'Projekt angelegt',
    'projects.deleted': 'Projekt gelöscht',
    'projects.timeBadge': 'Zeit',
    'projects.timeOnly': 'Reines Zeit-Projekt',
    'projects.editProject': 'Projekt bearbeiten',
    'projects.newProject': 'Neues Projekt',
    'projects.deleteExpenseTitle': 'Ausgabe löschen?',
    'projects.deleteIncomeTitle': 'Einnahme löschen?',
    'projects.deleteTxnMessage': '{date} · {amount} – wirklich löschen?',
    'projects.expensesIncome': 'Ausgaben & Einnahmen',
    'projects.timeOnlyHintBefore': 'Reines Zeit-Projekt. Erfasse Zeit über den',
    'projects.timeOnlyHintAfter': '; Ausgaben & Einnahmen werden hier nicht erfasst.',
    'projects.topExpenses': 'Größte Ausgaben',
    'projects.expense': 'Ausgabe',
    'projects.income': 'Einnahme',
    'projects.allCount': 'Alle ({n})',
    'projects.emptyTxns': 'Noch keine Einträge. Lege deine erste Ausgabe oder Einnahme an.',
    'projects.emptyFilter': 'Keine Einträge in dieser Kategorie.',
    'projects.balance': 'Bilanz',
    'projects.profit': 'Gewinn',
    'projects.inLoss': 'noch im Minus',
    'projects.breakEvenReached': 'Break-even erreicht am',
    'projects.remainingBefore': 'Noch',
    'projects.remainingAfter': 'bis zum Break-even.',
    'projects.newExpense': 'Neue Ausgabe',
    'projects.newIncome': 'Neue Einnahme',
    'projects.expensePlaceholder': 'z.B. 150',
    'projects.incomePlaceholder': 'z.B. 80',
    'projects.customCategoryPlaceholder': 'oder eigene Kategorie',
    'projects.expenseNotePlaceholder': 'z.B. Tinte Cyan nachbestellt',
    'projects.incomeNotePlaceholder': 'z.B. Auftrag Lisa – 5 Tassen',
    'projects.deleteProjectTitle': 'Projekt löschen?',
    'projects.deleteProjectMessage': '„{name}" und alle zugehörigen Einträge werden unwiderruflich gelöscht.',
    'projects.namePlaceholder': 'z.B. UV-Drucker',
    'projects.kind': 'Art',
    'projects.kindProfit': 'Gewinn & Zeit',
    'projects.kindProfitHint': 'Ausgaben/Einnahmen + Zeit',
    'projects.kindTime': 'Nur Zeit',
    'projects.kindTimeHint': 'Nur Zeit erfassen',
    'projects.icon': 'Symbol',
    'projects.deleteProject': 'Projekt löschen',
    'projects.errKindInvalid': 'Art ungültig',
    'projects.errProjectMissing': 'Projekt fehlt',

    // Tank-Liste -------------------------------------------------------------
    'fuel.title': 'Tank-Listen',
    'fuel.subtitle': 'Geteilte Listen für gemeinsame Autos.',
    'fuel.loadListsFailed': 'Konnte Tank-Listen nicht laden',
    'fuel.newLog': 'Neue Tank-Liste',
    'fuel.newLogMessage': 'Wie soll die Liste heißen? (z.B. „Auto Jana & Gerhard")',
    'fuel.logCreated': 'Tank-Liste angelegt',
    'fuel.createFailed': 'Anlegen fehlgeschlagen',
    'fuel.signInRequired': 'Bitte melde dich an, um Tank-Listen zu nutzen.',
    'fuel.emptyTitle': 'Noch keine Tank-Liste',
    'fuel.emptyHint': 'Erstelle eine neue Liste und schicke der Person, mit der du das Auto teilst, den Einladungs-Link.',
    'fuel.createLog': 'Tank-Liste anlegen',
    'fuel.owner': 'Inhaber',
    'fuel.markedPaid': 'Als bezahlt markiert',
    'fuel.undone': 'Zurückgesetzt',
    'fuel.undoFailed': 'Konnte nicht zurücksetzen',
    'fuel.membersOne': '1 Mitglied',
    'fuel.membersMany': '{n} Mitglieder',
    'fuel.emptyEntries': 'Noch keine Einträge. Trage deinen aktuellen Kilometerstand ein, um anzufangen.',
    'fuel.deleteEntryMessage': 'Das ändert die Abrechnung rückwirkend. Wirklich löschen?',
    'fuel.entrySaved': 'Eintrag gespeichert',
    'fuel.currentPeriod': 'Aktueller Zeitraum',
    'fuel.settled': 'Abgerechnet',
    'fuel.you': 'du',
    'fuel.deleteEntry': 'Eintrag löschen',
    'fuel.fillUp': 'Volltank',
    'fuel.paidBy': 'bezahlt von',
    'fuel.emptyPeriod': 'Noch keine Einträge in diesem Zeitraum.',
    'fuel.noData': 'Noch keine Daten. Trage einen ersten Kilometerstand ein.',
    'fuel.currentBalance': 'Aktueller Stand',
    'fuel.noFillUpYet': 'Noch nicht getankt — keine Abrechnung nötig.',
    'fuel.allSettled': 'Alles ausgeglichen 🎉',
    'fuel.undo': 'rückgängig',
    'fuel.someone': 'Jemand',
    'fuel.owes': 'schuldet',
    'fuel.markPaid': 'Als bezahlt markieren',
    'fuel.endGreaterThanStart': 'End-km muss größer als Start-km sein',
    'fuel.endGreaterThanStartKm': 'End-km muss größer als Start-km ({km}) sein.',
    'fuel.firstEntryHint': 'Erster Eintrag — bitte Start- und End-km angeben.',
    'fuel.newPeriodHint': 'Neuer Zeitraum nach dem Tanken.',
    'fuel.whoDrove': 'Wer ist gefahren?',
    'fuel.startKm': 'Start-km',
    'fuel.startKmPlaceholder': 'z.B. 124356',
    'fuel.lastFillUpAt': 'Letzter Volltank stand bei {km}.',
    'fuel.chainedStartHint': '(Ende vom letzten Eintrag)',
    'fuel.endKm': 'End-km',
    'fuel.endKmPlaceholder': 'z.B. 124500',
    'fuel.drivenPreview': '= {km} gefahren',
    'fuel.fillUpToggle': 'Volltank — Gesamtbetrag eintragen',
    'fuel.totalAmount': 'Gesamtbetrag ({currency})',
    'fuel.costPlaceholder': 'z.B. 78.40',
    'fuel.litersOptional': 'Liter (optional)',
    'fuel.litersPlaceholder': 'z.B. 42.30',
    'fuel.notePlaceholder': 'z.B. Autobahn nach Hamburg',
    'fuel.copyFailed': 'Konnte Link nicht kopieren',
    'fuel.renamed': 'Umbenannt',
    'fuel.deleteLogTitle': 'Tank-Liste löschen?',
    'fuel.deleteLogMessage': '„{name}" wird mit allen Einträgen gelöscht. Das kann nicht rückgängig gemacht werden.',
    'fuel.deleteForever': 'Endgültig löschen',
    'fuel.logDeleted': 'Liste gelöscht',
    'fuel.leaveLogTitle': 'Liste verlassen?',
    'fuel.leaveLogMessage': 'Du verlierst den Zugriff auf alle Einträge.',
    'fuel.leave': 'Verlassen',
    'fuel.leaveFailed': 'Verlassen fehlgeschlagen',
    'fuel.inviteLink': 'Einladungs-Link',
    'fuel.inviteHint': 'Wer den Link öffnet und sich anmeldet, wird automatisch hinzugefügt.',
    'fuel.errOdometerMissing': 'Kilometerstand fehlt',
    'fuel.errBothParties': 'Beide Beteiligte sind nötig',

    // Nachhilfe --------------------------------------------------------------
    'tutoring.semesterLabel': '{half}. HJ {start}/{end}',
    'tutoring.parentsOweMe': 'Eltern schulden mir',
    'tutoring.creditAvailable': 'Guthaben verfügbar',
    'tutoring.newStudent': 'Neuer Schüler',
    'tutoring.emptyStudents': 'Noch kein Schüler. Lege deinen ersten Schüler an, um Sitzungen zu erfassen.',
    'tutoring.archived': 'archiviert',
    'tutoring.settled': 'ausgeglichen',
    'tutoring.credit': 'Guthaben',
    'tutoring.hideArchived': 'Archivierte ausblenden',
    'tutoring.showArchived': 'Archivierte anzeigen',
    'tutoring.studentCreated': 'Schüler angelegt',
    'tutoring.studentNotFound': 'Schüler nicht gefunden',
    'tutoring.deleteSessionTitle': 'Sitzung löschen?',
    'tutoring.deleteSessionMessage': 'Sitzung am {date} ({duration}) wirklich löschen?',
    'tutoring.deletePaymentTitle': 'Zahlung löschen?',
    'tutoring.deletePaymentMessage': 'Zahlung von {amount} am {date} wirklich löschen?',
    'tutoring.activeAgain': 'Wieder aktiv',
    'tutoring.archivedToast': 'Archiviert',
    'tutoring.changeFailed': 'Konnte nicht ändern',
    'tutoring.deleteStudentTitle': 'Schüler löschen?',
    'tutoring.deleteStudentMessage': 'Wirklich "{name}" mit allen Sitzungen löschen? Das lässt sich nicht rückgängig machen.',
    'tutoring.prevSemester': 'Vorheriges Semester',
    'tutoring.nextSemester': 'Nächstes Semester',
    'tutoring.hours': 'Stunden',
    'tutoring.earned': 'Verdient',
    'tutoring.paidDirectly': 'Direkt bezahlt',
    'tutoring.session': 'Sitzung',
    'tutoring.payment': 'Zahlung',
    'tutoring.emptySemester': 'Keine Einträge in {semester}.',
    'tutoring.archive': 'Archivieren',
    'tutoring.deleteStudent': 'Schüler löschen',
    'tutoring.sessionSaved': 'Sitzung gespeichert',
    'tutoring.paymentRecorded': 'Zahlung erfasst',
    'tutoring.recordPayment': 'Zahlung erfassen',
    'tutoring.balanced': 'Ausgeglichen 🎉',
    'tutoring.creditHint': 'Wird mit den nächsten Sitzungen verrechnet.',
    'tutoring.owedHint': 'Eltern schulden dir diesen Betrag.',
    'tutoring.undoDirectPayment': 'Direktzahlung rückgängig',
    'tutoring.paidDirectlyHint': 'Direkt bezahlt (kein Guthaben)',
    'tutoring.togglePaid': 'Bezahlt-Status umschalten',
    'tutoring.paymentReceived': 'Zahlung erhalten',
    'tutoring.errNameRequired': 'Name ist nötig',
    'tutoring.editStudent': 'Schüler bearbeiten',
    'tutoring.namePlaceholder': 'z.B. Lukas',
    'tutoring.hourlyRate': 'Stundensatz (€/h)',
    'tutoring.ratePlaceholder': 'z.B. 25',
    'tutoring.colorAria': 'Farbe {color}',
    'tutoring.notesPlaceholder': 'z.B. Mathe Kl. 10, Eltern: Frau Müller, 0151…',
    'tutoring.errDurationInvalid': 'Dauer ungültig',
    'tutoring.editSession': 'Sitzung bearbeiten',
    'tutoring.newSession': 'Neue Sitzung',
    'tutoring.sessionNotePlaceholder': 'z.B. Klausur-Vorbereitung Algebra',
    'tutoring.editPayment': 'Zahlung bearbeiten',
    'tutoring.amountPlaceholder': 'z.B. 100',
    'tutoring.currentlyOpen': 'Aktuell offen: {amount}',
    'tutoring.paymentNotePlaceholder': 'z.B. bar von Mutter',
    'tutoring.errPickStudent': 'Schüler wählen',
    'tutoring.errRateInvalid': 'Stundensatz ungültig',

    // Fitness ----------------------------------------------------------------
    'fitness.deleteSportConfirm': 'Diese Sportart wirklich löschen?',
    'fitness.deleteRecordConfirm': 'Diesen Rekord löschen?',
    'fitness.yearsOne': '{n} Jahr',
    'fitness.yearsMany': '{n} Jahre',
    'fitness.monthsOne': '{n} Monat',
    'fitness.monthsMany': '{n} Monate',
    'fitness.daysOne': '{n} Tag',
    'fitness.daysMany': '{n} Tage',
    'fitness.overviewTitle': 'Fitness-Übersicht',
    'fitness.overviewSubtitle': 'Verfolge deine Workouts und Bestleistungen',
    'fitness.totalSports': 'Sportarten gesamt',
    'fitness.personalBests': 'Bestleistungen',
    'fitness.activeSports': 'Aktive Sportarten',
    'fitness.thisMonth': 'Diesen Monat',
    'fitness.yourSports': 'Deine Sportarten',
    'fitness.noSports': 'Noch keine Sportarten',
    'fitness.noSportsHint': 'Lege unter Sportarten deine erste Sportart an!',
    'fitness.recordsCount': '{n} Rekorde',
    'fitness.personalBest': 'Bestleistung',
    'fitness.noRecords': 'Noch keine Rekorde',
    'fitness.sportsTitle': 'Sportarten & Bestleistungen',
    'fitness.sportsSubtitle': 'Verwalte deine Sportarten und Rekorde',
    'fitness.addSport': 'Sportart hinzufügen',
    'fitness.addFirstSport': 'Erste Sportart hinzufügen',
    'fitness.startedOn': 'Seit: {date}',
    'fitness.achievedOn': 'Erreicht am {date}',
    'fitness.bestBadge': 'BESTE',
    'fitness.backToSports': 'Zurück zu Sportarten',
    'fitness.doingFor': 'Du machst diesen Sport seit',
    'fitness.totalWorkouts': 'Workouts gesamt',
    'fitness.personalRecords': 'Persönliche Rekorde',
    'fitness.workoutStructure': 'Workout-Struktur',
    'fitness.addRecord': 'Rekord hinzufügen',
    'fitness.lifts': 'Lifts',
    'fitness.gymnastics': 'Gymnastik / Körpergewicht',
    'fitness.gymnasticsShort': 'Gymnastik',
    'fitness.reps': '{n} Wdh.',
    'fitness.workouts': 'Workouts',
    'fitness.syncedWith': 'Synchronisiert mit {name}',
    'fitness.linkToCalendar': 'Mit Kalender verknüpfen',
    'fitness.labelFor': 'Label für {name}',
    'fitness.unlink': 'Verknüpfung aufheben',
    'fitness.logWorkout': 'Workout eintragen',
    'fitness.noWorkouts': 'Noch keine Workouts. Fang an, deinen Fortschritt zu verfolgen!',
    'fitness.minutes': 'Minuten',
    'fitness.addNewSport': 'Neue Sportart',
    'fitness.selectSport': 'Sportart wählen',
    'fitness.startedWhen': 'Wann hast du mit diesem Sport angefangen?',
    'fitness.editSport': 'Sportart bearbeiten',
    'fitness.sportName': 'Name der Sportart',
    'fitness.noStartDate': 'Kein Startdatum',
    'fitness.allRecords': 'Alle Rekorde ({n})',
    'fitness.pbBadge': 'PB',
    'fitness.noRecordsHint': 'Noch keine Rekorde. Fang an, deinen {name}-Fortschritt zu verfolgen!',
    'fitness.startTime': 'Startzeit',
    'fitness.durationPlaceholder': 'z. B. 60',
    'fitness.notesPlaceholder': 'Wie war das Workout?',
    'fitness.sectionWarmup': 'Aufwärmen',
    'fitness.sectionStrength': 'Kraft',
    'fitness.sectionWorkoutOfMonth': 'Workout des Monats',
    'fitness.sectionWod': 'WOD',
    'fitness.resultForTime': 'z. B. 12:34',
    'fitness.resultAmrap': 'z. B. 5+12 (Runden+Wdh.)',
    'fitness.resultEmom': 'z. B. 10 Min. geschafft',
    'fitness.resultTabata': 'z. B. Wdh. gesamt',
    'fitness.resultOther': 'Dein Ergebnis',
    'fitness.selectExerciseEllipsis': 'Übung wählen...',
    'fitness.selectExercise': 'Übung wählen',
    'fitness.detailsPlaceholder': 'z. B. 5x12',
    'fitness.workoutType': 'Workout-Typ...',
    'fitness.wodOther': 'Andere',
    'fitness.weightKg': 'Gewicht (kg)',
    'fitness.repsLabel': 'Wdh.',
    'fitness.timePlaceholder': 'Zeit (z. B. 2:30)',
    'fitness.deleteWorkout': 'Workout löschen',
    'fitness.structureManager': 'Workout-Struktur verwalten',
    'fitness.exerciseNamePlaceholder': 'Name der Übung (z. B. Wallballs)',
    'fitness.noExercises': 'Noch keine Übungen in {section}. Füge oben welche hinzu!',
    'fitness.editRecord': 'Rekord bearbeiten: {name}',
    'fitness.deleteRecord': 'Rekord löschen',
    'fitness.addPersonalRecord': 'Bestleistung hinzufügen',
    'fitness.allExercisesRecorded': 'Alle Übungen haben Rekorde! Stark!',

    // Geburtstagskalender ----------------------------------------------------
    'birthday.label': 'Geburtstag',
    'birthday.nthBirthday': '{n}. Geburtstag',
    'birthday.namePlaceholder': 'z. B. Lisa',
    'birthday.birthDate': 'Geburtsdatum',
    'birthday.day': 'Tag',
    'birthday.dayPlaceholder': 'TT',
    'birthday.month': 'Monat',
    'birthday.monthPlaceholder': 'MM oder Name',
    'birthday.year': 'Jahr',
    'birthday.yearPlaceholder': 'JJJJ',
    'birthday.formHintBefore': 'Mit Tab durch die Felder. Monat akzeptiert',
    'birthday.formHintOr': 'oder',
    'birthday.formHintAfter': '. Enter zum Speichern.',
    'birthday.tooManyDays': '{month} hat nur {n} Tage.',
    'birthday.sharedTitle': '{name} — Geburtstage',
    'birthday.addPerson': 'Person hinzufügen',
    'birthday.calendarHint': 'Geburtstage erscheinen im Kalender mit 🎁-Symbol in der gewählten Farbe.',
    'birthday.empty': 'Noch keine Geburtstage',
    'birthday.emptyHint': 'Klicke auf „Person hinzufügen", um loszulegen',
    'birthday.today': 'Heute!',
    'birthday.editPerson': 'Person bearbeiten',
  }
};

const I18nContext = createContext({
  lang: 'de',
  setLang: () => {},
  t: (k) => k,
  locale: de
});

const interpolate = (template, vars) => {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    vars[k] !== undefined ? String(vars[k]) : `{${k}}`
  );
};

// Look up a key in a given language. `t` from the hook is this bound to the
// active language; `translate` is the same lookup for non-React callers.
const lookup = (lang, key, varsOrFallback, maybeVars) => {
  let fallback;
  let vars;
  if (typeof varsOrFallback === 'string') {
    fallback = varsOrFallback;
    vars = maybeVars;
  } else {
    vars = varsOrFallback;
  }
  const table = dict[lang] || dict.de;
  const value = table[key];
  if (value !== undefined) return interpolate(value, vars);
  if (fallback !== undefined) return interpolate(fallback, vars);
  // Last resort: try the other language so we never show raw keys.
  const other = lang === 'de' ? dict.en[key] : dict.de[key];
  if (other !== undefined) return interpolate(other, vars);
  return key;
};

export const translate = (key, varsOrFallback, maybeVars) =>
  lookup(getLang(), key, varsOrFallback, maybeVars);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(getLang);

  useEffect(() => {
    try {
      window.localStorage.setItem(LANG_KEY, lang);
    } catch (_) {}
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', lang);
    }
  }, [lang]);

  const t = (key, varsOrFallback, maybeVars) =>
    lookup(lang, key, varsOrFallback, maybeVars);

  const setLang = (next) => {
    if (next === 'en' || next === 'de') setLangState(next);
  };

  const locale = localeFor(lang);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, locale }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => useContext(I18nContext);

// Sugar for the very common `format(d, 'MMMM', { locale })` call.
export const useDateLocale = () => useContext(I18nContext).locale;
